import './ComponentResults.css';
import React from "react";
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Grid from '@mui/material/Grid2';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import axiosInstance from '../axiosConfig';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate } from 'react-router-dom';
import API_URL from '../config';

const addUniqueIdentifierToComponent = (component) => ({
  ...component,
  uniqueId: uuidv4(), // Füge einen eindeutigen Identifier hinzu
});


export default function ComponentResults(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCreating, bookName: initialBookName, bookId: initialBookId } = location.state || {};
  const [searchText, setSearchText] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(0); // State für die aktuelle Seite
  const [bookPages, setBookPages] = React.useState([[[], []]]); // Initialisiere das Buch mit einer leeren Seite (linke und rechte Seite)
  const [tocComponent, setTocComponent] = React.useState({
    _id: 'toc-component',
    name: 'Inhaltsverzeichnis',
    uniqueId: uuidv4(),
    size: 1,
    description: 'Eine Inhaltsübersicht aller Komponenten im Schullogbuch.',
    svgImageUrl: '',
  });
  const [backPageComponent, setBackPageComponent] = React.useState({
    _id: 'back-page-component',
    name: 'Rückseite',
    uniqueId: uuidv4(),
    size: 1,
    description: 'Die Rückseite des Schullogbuchs.',
    pngImageUrl: '/uploads/components/png/Rückseite.png',
    svgImageUrl: '/components/svg/Rückseite.svg'
  });
  const [impressumComponent, setImpressumgComponent] = React.useState({
    _id: 'impressum-component',
    name: 'Impressum',
    uniqueId: uuidv4(),
    size: 1,
    description: 'Das Impressum des Schullogbuchs.',
    pngImageUrl: '/uploads/components/png/Impressum.png',
    svgImageUrl: '/components/svg/Impressum.svg'
  });
  const [bookName, setBookName] = React.useState(initialBookName || '');
  const [bookId, setBookId] = React.useState(initialBookId || '');
  const [componentSizes, setComponentSizes] = React.useState({});

  // get basic components from backend
  const [basicComponents, setBasicComponents] = React.useState([]);
  const [schoolProfile, setSchoolProfile] = React.useState(null);
  const [alert, setAlert] = React.useState(null);
  const isTocUpdatingRef = React.useRef(false);
  const [expandedAccordionBasic, setExpandedAccordionBasic] = React.useState(null);
  const [expandedAccordionCurrentChoice, setExpandedAccordionCurrentChoice] = React.useState(null);
  const [highlightedAreas, setHighlightedAreas] = React.useState([]); // Hervorhebungsbereiche
  const [primaryPath, setPrimaryPath] = React.useState(props.data?.primary || '');
  const [secondaryPath, setSecondaryPath] = React.useState(props.data?.secondary || []);
  const [tertiaryPath, setTertiaryPath] = React.useState(props.data?.tertiary || []);
  const [isBookLoaded, setIsBookLoaded] = React.useState(false);
  const [isBookLoading, setIsBookLoading] = React.useState(false);
  const [isPathLoading, setIsPathLoading] = React.useState(true);
  const [isBasicComponentsLoading, setIsBasicComponentsLoading] = React.useState(true);
  const [selectedClasses, setSelectedClasses] = React.useState([]);
  const prevBookPagesRef = React.useRef(bookPages);
  const prevCurrentPageRef = React.useRef(currentPage);
  const [highlightedComponent, setHighlightedComponent] = React.useState(null);
  const nextPageTriggerRef = React.useRef(false);
  const blockedIndexesRef = React.useRef([]);
  const [draggingComponent, setDraggingComponent] = React.useState(null);
  const [shouldHighlightCurrentChoice, setShouldHighlightCurrentChoice] = React.useState(false);
  const [dragPreview, setDragPreview] = React.useState(null);

  React.useEffect(() => {
    if (draggingComponent && draggingComponent.pngImageUrl) {
      const img = new Image();
      img.src = draggingComponent.pngImageUrl;
      img.onload = () => setDragPreview(img); // Sobald das Bild geladen ist, speichern wir es
    }
  }, [draggingComponent]);


  const classColors = {
    "5-7": "custom1.main",
    "8-9": "custom2.main",
    "10-11": "custom3.main",
    "12-13": "custom4.main"
  };
  const classColorsLight = {
    "5-7": "custom1.light",
    "8-9": "custom2.light",
    "10-11": "custom3.light",
    "12-13": "custom4.light"
  };

  //TODO: enable again when productive
  // React.useEffect(() => {
  //   const handleContextmenu = e => {
  //       e.preventDefault()
  //   }
  //   document.addEventListener('contextmenu', handleContextmenu)
  //   return function cleanup() {
  //       document.removeEventListener('contextmenu', handleContextmenu)
  //   }
  // }, [ ])

  const appendTimestamp = (url) => {
    const baseUrl = url.split('?')[0]; // Entfernt vorhandene Query-Parameter
    return `${baseUrl}?t=${new Date().getTime()}`;
  };

  const generateTableOfContents = async () => {
    const token = localStorage.getItem('authToken');
    
    try {
      const res = await axiosInstance.post(
        '/doc/generate-toc',
        { bookPages, bookName },
        { headers: { Authorization: `${token}` } }
      );
      const newImageUrls = res.data.imageUrls.map((url) => appendTimestamp(url));
      setTocComponent((prev) => ({
        ...prev,
        svgImageUrl: res.data.svgImageUrl,
        pngImageUrl: newImageUrls[0],
        allPngImageUrls: newImageUrls,
      }));
      isTocUpdatingRef.current = true;
    } catch (error) {
      console.error('Fehler beim Erstellen des Inhaltsverzeichnisses:', error);
    }
  };

  /**
   * Hilfsfunktion, die leere Doppelseiten entfernt.
   * Doppelseiten gelten als leer, wenn sowohl links als auch rechts keine Komponenten liegen,
   * aber die vorherige Seite darf unter Umständen kein Inhaltsverzeichnis sein.
   */
  function removeEmptyDoublePages(pages, tocSize) {
    return pages.filter(([_left, _right], pageIndex, allPages) => {
        const left = _left;
        const right = _right;

        // Falls die Seite nicht leer ist, behalten wir sie
        if (left.length > 0 || right.length > 0) {
            return true;
        }

        // Falls tocSize gerade ist, prüfe die Inhaltsverzeichnis-Regel
        if (tocSize % 2 === 0 && pageIndex > 0) {
            const prevRight = allPages[pageIndex - 1][1]; // Rechte Seite der vorherigen Doppelseite

            // Prüfe, ob eine Komponente mit "Inhaltsverzeichnis" auf der rechten Seite der vorherigen Seite existiert
            const hasTocComponent = prevRight.some(comp => comp.name.startsWith("Inhaltsverzeichnis"));

            // Falls ja, darf diese leere Doppelseite nicht entfernt werden
            if (hasTocComponent) {
                return true;
            }
        }

        // Falls alle Bedingungen nicht erfüllt sind, entfernen wir die Doppelseite
        return false;
    });
  }

  function shiftDoublePageForward(updatedPages, fromPageIndex, groupUniqueIds = []) {
    /**
     * Dieses neue shiftDoublePageForward verschiebt nur die Inhalte, 
     * die NICHT zu den groupUniqueIds gehören. So wird verhindert,
     * dass das TOC selbst (oder das Duplikat) erneut kopiert wird.
     */
  
    const [left, right] = updatedPages[fromPageIndex];
    
    // Ziel-Doppelseite anlegen, falls sie noch nicht existiert
    if (!updatedPages[fromPageIndex + 1]) {
      updatedPages[fromPageIndex + 1] = [[], []];
    }
  
    // Inhalte identifizieren, die verschoben werden sollen
    // (Alle, die nicht zum TOC gehören und nicht in groupUniqueIds auftauchen)
    const leftToShift = left.filter((comp) => !groupUniqueIds.includes(comp.uniqueId));
    const rightToShift = right.filter((comp) => !groupUniqueIds.includes(comp.uniqueId));
  
    // Falls die nächste Seite ebenfalls belegt ist, müssen wir die
    // dortigen Inhalte zunächst weiter nach hinten schieben (rekursiv).
    if (
      updatedPages[fromPageIndex + 1][0].length > 0 ||
      updatedPages[fromPageIndex + 1][1].length > 0
    ) {
      shiftDoublePageForward(updatedPages, fromPageIndex + 1, groupUniqueIds);
    }
  
    // Verschiebe nur die "fremden" Inhalte auf die nächste Doppelseite
    updatedPages[fromPageIndex + 1][0].push(...leftToShift);
    updatedPages[fromPageIndex + 1][1].push(...rightToShift);
  
    // Entferne diese verschobenen Inhalte aus der ursprünglichen Seite
    updatedPages[fromPageIndex][0] = left.filter((comp) => groupUniqueIds.includes(comp.uniqueId));
    updatedPages[fromPageIndex][1] = right.filter((comp) => groupUniqueIds.includes(comp.uniqueId));
  }

  /**
   * Diese Funktion fügt *ein* neues Inhaltsverzeichnis-Duplikat (newTocDuplicate)
   * an die nächste Seite/Seite-Seite an (also Zick-Zack).
   * Wenn dort schon eine Komponente ist, wird die zuerst nach hinten geschoben (shiftOneSideForward).
   */
  function insertOneTocDuplicate(updatedPages, startPageIndex, startSideIndex, originalTocComponent, newTocDuplicate) {
    let nextPageIndex = startPageIndex;
    let nextSideIndex = startSideIndex === 0 ? 1 : 0; // links->rechts, rechts->links

    // Falls wir von rechts kamen, gehe eine Seite weiter (auf die linke Seite)
    if (startSideIndex === 1) {
      nextPageIndex++;
      if (!updatedPages[nextPageIndex]) {
        updatedPages[nextPageIndex] = [[], []];
      }
    }

    // Sicherstellen, dass die Zielseite existiert
    if (!updatedPages[nextPageIndex]) {
      updatedPages[nextPageIndex] = [[], []];
    }

    // old if condition
    // if (updatedPages[nextPageIndex][nextSideIndex].length > 0) {

    // new if condition - shift one double page forward in case we want to insert on the right page
    // independantly if there is content or not
    if (nextSideIndex === 1) {
      // Wir übergeben dem shift die uniqueId(s) des TOC und seiner Duplikate,
      // damit nicht versehentlich das TOC nochmal kopiert/verschoben wird.
      const excludeIds = [
        originalTocComponent.uniqueId,               // das Original
        ...(originalTocComponent.duplicateIds || []), // bereits vorhandene Duplikate
        newTocDuplicate.uniqueId                     // das neu erzeugte Duplikat
      ];
      shiftDoublePageForward(updatedPages, nextPageIndex, excludeIds);  
    }
  

    // Jetzt ist Platz und wir können das Duplikat einfügen
    updatedPages[nextPageIndex][nextSideIndex].push(newTocDuplicate);

    // Zurückgeben, wo wir das Duplikat tatsächlich abgelegt haben.
    return { updatedPages, pageIndex: nextPageIndex, sideIndex: nextSideIndex };
  }


  const updateTocComponentInBookPages = (bookPages, prevTocComponent, tocComponent) => {
    let updatedPages = [...bookPages];
    const prevTocSize = prevTocComponent.allPngImageUrls.length;
    const newTocSize = tocComponent.allPngImageUrls.length;

    // 1. FALL: Inhaltsverzeichnis HAT SICH VERKLEINERT
    //    -------------------------------------------
    if (newTocSize < prevTocSize) {
      const originalTocComponent = prevTocComponent;

      if (!originalTocComponent || !originalTocComponent.duplicateIds) {
        return updatedPages;
      }

      const duplicateIdsToRemove = originalTocComponent.duplicateIds.slice(newTocSize - 1);

      const newPages = updatedPages.map(([left, right]) => [
        left.filter((comp) => !duplicateIdsToRemove.includes(comp.uniqueId)),
        right.filter((comp) => !duplicateIdsToRemove.includes(comp.uniqueId)),
      ]);


      // remove all empty double pages
      const cleanedPages = removeEmptyDoublePages(newPages, newTocSize);

      // Aktualisiere duplicateIds beim Original
      const updatedBookPages = cleanedPages.map((page) =>
        page.map((side) =>
          side.map((comp) =>
            comp.uniqueId === originalTocComponent.uniqueId
              ? {
                  ...comp,
                  // Filtere die entfernten IDs raus
                  duplicateIds: originalTocComponent.duplicateIds.filter(
                    (id) => !duplicateIdsToRemove.includes(id)
                  ),
                  // update allPngImageUrls
                  allPngImageUrls: tocComponent.allPngImageUrls,
                  // Falls nur noch 1 übrig ist, isGroupHeader, duplicateIds entfernen und allPngImageUrls aktualisieren.
                  ...(newTocSize === 1
                    ? (() => {
                        const { isGroupHeader, duplicateIds, ...newComp } = comp;
                        newComp.allPngImageUrls = tocComponent.allPngImageUrls;
                        return newComp;
                      })()
                    : {}),                  
                }
              : comp
          )
        )
      );

      removeEmptyPagesAfterToc(updatedBookPages);

      return updatedBookPages;
    }

    // 2. FALL: Inhaltsverzeichnis HAT SICH VERGRÖSSERT
    //    ---------------------------------------------
    if (newTocSize > prevTocSize) {

      const tocPosition = getComponentPositionInBookPages(prevTocComponent);
      if (!tocPosition) {
        return updatedPages; // falls wir gar keine Position finden...
      }

      const { pageIndex, sideIndex } = tocPosition;

      // get duplicate ids from original toc component in bookpages
      const originalTocComponent = prevTocComponent;
      const duplicateIds = originalTocComponent?.duplicateIds ? [...originalTocComponent.duplicateIds] : [];


      // Wir erzeugen alle zusätzlichen Duplikate einzeln
      // – z. B. wenn prevTocSize=2 und newTocSize=4, haben wir 2 neue Duplikate.
      for (let i = prevTocSize; i < newTocSize; i++) {
        // Neues Duplikat-Objekt anlegen
        const newTocDuplicate = {
          ...tocComponent,
          uniqueId: uuidv4(),
          isDuplicate: true,
          pngImageUrl: tocComponent.allPngImageUrls[i],
        };

        // Duplikat einfügen: immer "die nächste Seite" (Zick-Zack) vom Original oder vom letzten Duplikat
        // Falls wir schon Duplikate eingefügt haben, immer von deren letzter Position ausgehen.
        let referencePageIndex = pageIndex;
        let referenceSideIndex = sideIndex;

        if (duplicateIds.length > 0) {
          // letzte Duplikat-ID
          const lastDupId = duplicateIds[duplicateIds.length - 1];
          // Position des letzten Duplikats ermitteln
          const lastDupPosition = getComponentPositionInBookPages({ uniqueId: lastDupId }, updatedPages);
          if (lastDupPosition) {
            referencePageIndex = lastDupPosition.pageIndex;
            referenceSideIndex = lastDupPosition.sideIndex;
          }
        }

        // Füge den neuen Duplikat an der korrekten "nächsten" Stelle ein
        const insertResult = insertOneTocDuplicate(
          updatedPages,
          referencePageIndex,
          referenceSideIndex,
          originalTocComponent,
          newTocDuplicate
        );
        updatedPages = insertResult.updatedPages;
        duplicateIds.push(newTocDuplicate.uniqueId);
      }

      // ### NEU/GEÄNDERT ###
      // Der Original-TOC wird zur Gruppe (isGroupHeader = true), duplicateIds anhängen
      updatedPages = updatedPages.map((page, pIdx) => {
        return page.map((side, sIdx) => {
          return side.map((comp) => {
            if (comp.uniqueId === originalTocComponent.uniqueId) {
              return {
                ...comp,
                isGroupHeader: true,
                duplicateIds: duplicateIds,
                // update allPngImageUrls
                allPngImageUrls: tocComponent.allPngImageUrls,
              };
            }
            return comp;
          });
        });
      });

      // Entferne ggf. leere Doppelseiten, falls beim Verschieben ein Seitenteil leer wurde
      // updatedPages = removeEmptyDoublePages(updatedPages);

      return updatedPages;
    }

    // Wenn sich nichts ändert
    return updatedPages;
  };

  function removeEmptyPagesAfterToc(updatedPages) {
    /**
     * Entfernt zwei aufeinanderfolgende leere Seiten (also eine halbe Doppelseite),
     * wenn sie direkt nach einer Komponente mit Namen "Inhaltsverzeichnis" auftreten.
     */
    
    for (let pageIndex = 0; pageIndex < updatedPages.length - 1; pageIndex++) {
      const [leftPage, rightPage] = updatedPages[pageIndex];
  
      // Prüfe, ob die linke Seite ein Inhaltsverzeichnis ist
      const isTocOnLeft = leftPage.some(comp => comp.name.startsWith("Inhaltsverzeichnis"));
  
      if (isTocOnLeft) {
        const nextPage = updatedPages[pageIndex + 1];
  
        // Prüfe, ob die nächsten beiden Seiten leer sind
        const isRightEmpty = rightPage.length === 0;
        const isNextLeftEmpty = nextPage[0].length === 0;
  
        if (isRightEmpty && isNextLeftEmpty) {
          // Entferne beide leeren Seiten
          updatedPages[pageIndex][1] = updatedPages[pageIndex + 1][1]; // Grußwort rutscht hoch
          updatedPages.splice(pageIndex + 1, 1); // Doppelseite löschen
          break; // Verhindert, dass wir aus Versehen mehrere Seiten löschen
        }
      }
    }
  }

  React.useEffect(() => {
    // Prüfe, ob eine Komponente mit dem Namen "Inhaltsverzeichnis" in den selectedComponents ist
    let tocSizeDiff = 0;
    const prevTocComponent = findTocComponentInBookPages(bookPages);

    if (prevTocComponent) {
      tocSizeDiff = Number(prevTocComponent.allPngImageUrls.length) - Number(tocComponent.allPngImageUrls.length);
      // update bookPages in case the size of table of contents has changed
      if (tocSizeDiff !== 0) {
        const updatedPages = updateTocComponentInBookPages(bookPages, prevTocComponent, tocComponent);
        setBookPages(updatedPages);
      }
    }
  }, [tocComponent]);

  React.useEffect(() => {
    if (!isBasicComponentsLoading && !nextPageTriggerRef.current && tocComponent.allPngImageUrls) {
      forceSpecialComponentsOnFirstPages();
      saveBookState();
    }
  }, [isBasicComponentsLoading, tocComponent]);

  const forceSpecialComponentsOnFirstPages = () => {
    const requiredComponents = [
      { pageIndex: 0, sectionIndex: 0, name: "Rückseite", component: backPageComponent },
      { pageIndex: 0, sectionIndex: 1, name: "Deckblatt" },
      { pageIndex: 1, sectionIndex: 0, name: "Impressum" },
      { pageIndex: 2, sectionIndex: 0, name: "Inhaltsverzeichnis", component: tocComponent }
    ];
  
    let updatedBookPages = [...bookPages];
    let bookUpdated = false;
  
    requiredComponents.forEach(({ pageIndex, sectionIndex, name, component }) => {
      if (!updatedBookPages[pageIndex]) {
        updatedBookPages[pageIndex] = [[], []];
        bookUpdated = true;
      }
  
      const componentExists = updatedBookPages[pageIndex][sectionIndex].some(comp => comp.name.startsWith(name));
      if (!componentExists) {
        let newComponent = component || basicComponents.find(c => c.name.startsWith(name));
        if (newComponent) {
          updatedBookPages[pageIndex][sectionIndex].push(newComponent);
          setBasicComponents(prev => {
            const updatedComponents = prev.filter(c => c.name !== newComponent.name);
            return updatedComponents;
          });          
          bookUpdated = true;
        }
      }
    });
  
    if (bookUpdated) {
      nextPageTriggerRef.current = true;
      setBookPages(updatedBookPages);
    }
  };

  React.useEffect(() => {
    if (primaryPath && secondaryPath.length > 0 && tertiaryPath.length > 0) {
      setIsPathLoading(false); // Ladezustand abschließen
    }
  }, [primaryPath, secondaryPath, tertiaryPath]);

  const getComponentCount = (component) => {
    return bookPages.reduce((acc, [left, right]) => {
      const countInLeft = left.filter((c) => c.name === component.name).length;
      const countInRight = right.filter((c) => c.name === component.name).length;
      return acc + countInLeft + countInRight;
    }, 0);
  };

  React.useEffect(() => {
    if (highlightedComponent) {
      const element = document.getElementById(`selected-${highlightedComponent.uniqueId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedComponent]);

  React.useEffect(() => {
    if (highlightedComponent) {
      // Setze nach 3 Sekunden den State auf null zurück
      const timer = setTimeout(() => {
        setHighlightedComponent(null);
      }, 1000);
  
      // Cleanup: Falls sich `highlightedComponent` ändert, lösche den alten Timer
      return () => clearTimeout(timer);
    }
  }, [highlightedComponent]);  

  const getBookPagesDiff = (oldBookPages, newBookPages) => {
    const pageDiffs = [];
    let hasChanges = false;

    // Ermitteln wir die max. Seitenanzahl, damit wir in beiden Arrays sauber iterieren
    const maxLength = Math.max(oldBookPages?.length || 0, newBookPages?.length || 0);

    for (let pageIndex = 0; pageIndex < maxLength; pageIndex++) {
      // Falls eine Seite in old/new nicht existiert, ersetzen wir sie durch leere Arrays
      const oldPage = oldBookPages[pageIndex] || [[], []];
      const newPage = newBookPages[pageIndex] || [[], []];

      // oldPage[0], oldPage[1] = linke bzw. rechte Seite alt
      // newPage[0], newPage[1] = linke bzw. rechte Seite neu
      for (let side = 0; side < 2; side++) {
        const oldComponents = oldPage[side] || [];
        const newComponents = newPage[side] || [];

        // Extrahiere alle uniqueIds
        const oldIds = oldComponents.map(comp => comp.uniqueId);
        const newIds = newComponents.map(comp => comp.uniqueId);

        // Finde hinzugekommene und entfernte IDs
        const removed = oldIds.filter(id => !newIds.includes(id));
        const added = newIds.filter(id => !oldIds.includes(id));

        if (removed.length > 0 || added.length > 0) {
          hasChanges = true;
          pageDiffs.push({
            pageIndex,
            side,
            added,
            removed
          });
        }
      }
    }

    return {
      pageDiffs,
      hasChanges
    };
  }

  React.useEffect(() => {
    if (basicComponents.length > 0) {
      setIsBasicComponentsLoading(false); // Ladezustand abschließen
    }
  }, [basicComponents]);  

  React.useEffect(() => {
    if (isTocUpdatingRef.current) {
      isTocUpdatingRef.current = false; // Flag zurücksetzen
      return; // Hook nicht ausführen
    }
    const diff = getBookPagesDiff(prevBookPagesRef.current, bookPages);
    const hasBookPagesChanged = diff.hasChanges;
    const hasCurrentPageChanged = prevCurrentPageRef.current !== currentPage;

    prevBookPagesRef.current = bookPages;
    prevCurrentPageRef.current = currentPage;

    // in case hook is only triggered by currenrPage and toc is not visible on current page, do not generate toc
    if (!isTocVisibleOnCurrentPage() && hasCurrentPageChanged && !hasBookPagesChanged) {
      return; // Hook nicht ausführen
    }
  
    generateTableOfContents();
  }, [bookPages, currentPage]);

  React.useEffect(() => {
    if (!nextPageTriggerRef.current) {
      saveBookState();
    }
  }, [bookPages]);

  React.useEffect(() => {
    const maxPageIndex = bookPages.length - 1;
    const newPageIndex = Math.floor(currentPage / 2);
  
    // Falls die aktuelle Seite außerhalb des gültigen Bereichs ist, setze currentPage auf 0
    if (newPageIndex > maxPageIndex || !bookPages[newPageIndex]) {
      console.warn(`currentPage (${currentPage}) existiert nicht. Setze auf 0 zurück.`);
      setCurrentPage(0);
    }
  }, [currentPage, bookPages]);  

  React.useEffect(() => {
    const token = localStorage.getItem('authToken');

    axiosInstance
      .get('/profile/school-profile', {
        headers: {
          Authorization: `${token}`,
        },
      })
      .then((response) => {
        if (response.data.profile) {
          setSchoolProfile(response.data.profile);
        }
      })
      .catch((error) => {
        console.error('Fehler beim Abrufen des Schulprofils:', error);
      });
  }, []);

  React.useEffect(() => {
    if (location.state?.editWithNewSelection) {
      setPrimaryPath(location.state.primary);
      setSecondaryPath(location.state.secondaries);
      setTertiaryPath(location.state.tertiaries);
      return;
    } else if ( !schoolProfile || !props?.data || isBookLoading) {
      return;
    }
    const fetchAllBasicComponents = async () => {
      const token = localStorage.getItem('authToken');
      try {
        // Nur tertiary wird übergeben
        const { secondary } = props.data;
        const { tertiary } = props.data;
        
        // Sende die Daten als JSON-String im Query-String
        const queryParams = new URLSearchParams();
        queryParams.append('secondary', JSON.stringify(secondary));
        queryParams.append('tertiary', JSON.stringify(tertiary));
        
        const res = await axiosInstance.get(
          `/components?${queryParams.toString()}`,
          { headers: { Authorization: `${token}` } }
        );
        setBasicComponents(sortBasicComponents(res.data));      
      } catch (err) {
        console.log(err);
      }
    };
  
    fetchAllBasicComponents();
  }, [schoolProfile, props.data, location.state?.editWithNewSelection]);
  

  const removeFromCurrentChoice = (component) => {
    // Speichere den aktuellen Stand der Buchseiten vor der Entfernung
    const previousBookPages = [...bookPages];

    let updatedPages;
  
    // Prüfen, ob die Komponente eine Gruppenkomponente ist
    if (component.isGroupHeader && Array.isArray(component.duplicateIds)) {
        // Entferne den Gruppen-Header und alle Duplikate aus den Buchseiten
        updatedPages = bookPages.map(([leftPage, rightPage]) => [
            leftPage.filter(
                (item) =>
                    item.uniqueId !== component.uniqueId &&
                    !component.duplicateIds.includes(item.uniqueId)
            ),
            rightPage.filter(
                (item) =>
                    item.uniqueId !== component.uniqueId &&
                    !component.duplicateIds.includes(item.uniqueId)
            ),
        ]);
    } else {
        // Standardfall: Entferne nur die einzelne Komponente
        updatedPages = bookPages.map(([leftPage, rightPage]) => [
            leftPage.filter((item) => item.uniqueId !== component.uniqueId),
            rightPage.filter((item) => item.uniqueId !== component.uniqueId),
        ]);
    }

    // Entferne nur die Doppelseiten, die erst nach dem Entfernen leer geworden sind
    updatedPages = updatedPages.filter((page, index) => {
        const wasPreviouslyOccupied = previousBookPages[index][0].length > 0 || previousBookPages[index][1].length > 0;
        const isNowEmpty = page[0].length === 0 && page[1].length === 0;
        return !(wasPreviouslyOccupied && isNowEmpty); // Entferne nur die Seiten, die vorher Inhalte hatten
    });

    setBookPages(updatedPages);

    // Falls die Komponente NICHT recurring ist, füge sie zurück zur Liste der Basiskomponenten hinzu
    if (!component.recurring) {
        setBasicComponents((prevBasicComponents) => sortBasicComponents([...prevBasicComponents, component]));
    }
};

  const findComponentInBookPages = (uniqueId, bookPages) => {
    for (const page of bookPages) {
      for (const side of page) {
        const component = side.find((comp) => comp.uniqueId === uniqueId);
        if (component) {
          return component; // Gibt die gefundene Komponente zurück
        }
      }
    }
    return null; // Gibt null zurück, wenn keine Komponente gefunden wurde
  };

  const findTocComponentInBookPages = (bookPages) => {
    for (const page of bookPages) {
      for (const side of page) {
        const component = side.find((comp) => comp.name.startsWith("Inhaltsverzeichnis") && comp?.allPngImageUrls.length > 0);
        if (component) {
          return component; // Gibt die gefundene Komponente zurück
        }
      }
    }
    return null; // Gibt null zurück, wenn keine Komponente gefunden wurde
  };

  const getComponentPositionInBookPages = (component) => {
    for (let pageIndex = 0; pageIndex < bookPages.length; pageIndex++) {
        const [leftPage, rightPage] = bookPages[pageIndex];

        // Prüfe zuerst die linke Seite
        const leftIndex = leftPage.findIndex(comp => comp.uniqueId === component.uniqueId);
        if (leftIndex !== -1) {
            return { pageIndex, sideIndex: 0, indexOnSide: leftIndex }; // 0 = linke Seite
        }

        // Prüfe die rechte Seite
        const rightIndex = rightPage.findIndex(comp => comp.uniqueId === component.uniqueId);
        if (rightIndex !== -1) {
            return { pageIndex, sideIndex: 1, indexOnSide: rightIndex }; // 1 = rechte Seite
        }
    }

    return null; // Falls nicht gefunden
  };

  const defineAllBlockedIndexes = () => {
    // iterate over bookPages and add all positions of components with isDuplicate=true to blocked indexes
    const allBlockedIndexes = [];
    bookPages.forEach((page, pageIndex) => {
      page.forEach((side, sideIndex) => {
        side.forEach((component, indexOnSide) => {
          if (component.isDuplicate) {
            const componentPosition = getComponentPositionInBookPages(component);
            allBlockedIndexes.push(componentPosition);

            // Prüfe, ob diese Komponente die letzte in der aktuellen Seite ist
            const isLastOnSide = indexOnSide === side.length - 1;

            if (isLastOnSide) {
              let nextPageIndex = pageIndex;
              let nextSideIndex = sideIndex === 0 ? 1 : 0;
              let nextIndexOnSide = 0;

              // Falls die Komponente auf sideIndex 1 ist, gehe zur nächsten Seite
              if (sideIndex === 1) {
                nextPageIndex++;
              }

              // Prüfe, ob die nächste Seite existiert und ob die erste Komponente auf dieser Seite ebenfalls isDuplicate ist
              if (bookPages[nextPageIndex] && bookPages[nextPageIndex][nextSideIndex]?.[nextIndexOnSide]?.isDuplicate) {
                allBlockedIndexes.push({
                  ...componentPosition,
                  indexOnSide: componentPosition.indexOnSide + 1
                });
              }
            }
          }
        });
      });
    });

    blockedIndexesRef.current = allBlockedIndexes;
  };

  const onDragStart = async (start) => {
    let componentList = [];
    let draggedComponent = null;
    defineAllBlockedIndexes();
    if (start.draggableId.includes("basicComponent")) {
      componentList = basicComponents;
      draggedComponent = componentList.find((component) => 
        component.name === start.draggableId.split('draggable-basicComponentList-')[1]
      );
    } else if (start.draggableId.includes("left-") || start.draggableId.includes("right-") || start.draggableId.includes("draggable-selectedComponentList")) {
      const uniqueId = start.draggableId.split('uniqueId-')[1];
      draggedComponent = findComponentInBookPages(uniqueId, bookPages);
    }

    if (start.draggableId.includes("draggable-selectedComponentList")) {
      setShouldHighlightCurrentChoice(true);
    }

    if (start.draggableId.includes("draggable-basicComponentList") || start.draggableId.includes("left-") || start.draggableId.includes("right-")) {
      setShouldHighlightCurrentChoice(false);
      setDraggingComponent(draggedComponent);
    } else {
      setDraggingComponent(null);
    }

     // store component size in global state - necessary for dragging a component
     setComponentSizes(prevSizes => ({
      ...prevSizes,
      [draggedComponent.name]: draggedComponent.size
    }));
  };

  const onDragEnd = (result) => {
    // Reset highlighted areas
    setHighlightedAreas([]);
  
    const { source, destination } = result;
  
    if (!destination) return;
  
    // Kein Wechsel, wenn die Quelle und das Ziel gleich sind
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }
  
    const sourceId = source.droppableId;
    const destinationId = destination.droppableId;
  
    // Dispatch je nach Use-Case
    if (sourceId === 'droppable-list' && destinationId === 'droppable-list') {
      handleListToListDrag();
    } else if (sourceId.includes('droppable-page') && destinationId === 'droppable-list') {
      handlePageToListDrag();
    } else if (sourceId.includes('droppable-page') && destinationId.includes('droppable-currentChoice-')) {
      handlePageToCurrentChoiceDrag();
    } else if (sourceId.includes('droppable-currentChoice-') && destinationId === 'droppable-list') {
      handleCurrentChoiceToListDrag();
    } else if (sourceId.includes('droppable-currentChoice-') && destinationId.includes('droppable-page')) {
      handleCurrentChoiceToPageDrag();
    } else if (sourceId === 'droppable-list' && destinationId.includes('droppable-page')) {
      handleListToPageDrag(result);
    } else if ((sourceId.includes('droppable-page') && destinationId.includes('droppable-page'))) {
      // TODO: check for groups and try to integrate handleCurrentChoiceToCurrentChoiceDrag(result);
      handlePageToPageDrag(result);
    } else if (sourceId.includes('droppable-currentChoice-') && destinationId.includes('droppable-currentChoice-')) {
      handleCurrentChoiceToCurrentChoiceDrag(result);
    } else {
      console.warn('Ungültiger Drag-and-Drop Use-Case');
    }
  };

  const handleListToListDrag = () => {
    setAlert({
      severity: "warning",
      message: "Das Verschieben innerhalb der empfohlenen Komponenten ist nicht erlaubt."
    });
    console.warn('Verschieben zwischen droppable-list und droppable-list ist nicht erlaubt.');
  };

  const handlePageToListDrag = () => {
    setAlert({
      severity: "warning",
      message: "Das Verschieben von der Buchvorschau zu den empfohlenen Komponenten ist nicht erlaubt."
    });
    console.warn('Verschieben von droppable-page zu droppable-list ist nicht erlaubt.');
  };

  const handlePageToCurrentChoiceDrag = () => {
    setAlert({
      severity: "warning",
      message: "Das Verschieben von der Buchvorschau zur aktuellen Auswahl ist nicht erlaubt."
    });
    console.warn('Verschieben von droppable-page zu droppable-currentChoice ist nicht erlaubt.');
  };

  const handleCurrentChoiceToListDrag = () => {
    setAlert({
      severity: "warning",
      message: "Das Verschieben von der aktuellen Auswahl zu den empfohlenen Komponenten ist nicht erlaubt."
    });
    console.warn('Verschieben von droppable-current-choice zu droppable-list ist nicht erlaubt.');
  };

  const handleCurrentChoiceToPageDrag = () => {
    setAlert({
      severity: "warning",
      message: "Das Verschieben von der aktuellen Auswahl zur Buchvorschau ist nicht erlaubt."
    });
    console.warn('Verschieben von droppable-current-choice zu droppable-page ist nicht erlaubt.');
  };

  const pageIsBlocked = (result) => {
    const tocLength = tocComponent?.allPngImageUrls.length;
    const droppablePageNumber = Number(result.destination.droppableId.split('-')[2]);
    let blockedPageNumbers = [0, 1, 2, 3, 4];
    if (tocLength > 1) {
      blockedPageNumbers.push(...[5, 6]);
    }
    if (blockedPageNumbers.includes(droppablePageNumber)) {
      setAlert({
        severity: "warning",
        message: "Aufgrund von drucktechnischen Vorgaben kann auf diese Seite nichts platziert werden."
      });
      return true;
    }
    return false;
  }

  const handleListToPageDrag = (result, provided) => {
    // do not allow to drop in case current page is a blocked page
   if (pageIsBlocked(result)) return;

    const { source, destination } = result;
    const componentName = result.draggableId.split('draggable-basicComponentList-')[1];
    const fromIndex = basicComponents.findIndex((c) => c.name === componentName);
    if (fromIndex === -1) return; // Sicherheitshalber
    const selectedComponent = basicComponents[fromIndex];

    const getPageDetails = (droppableId) => {
      const pageIndex = Math.floor(parseInt(droppableId.split('-')[2]) / 2);
      const pageSide = parseInt(droppableId.split('-')[2]) % 2 === 0 ? 0 : 1; // Links = 0, Rechts = 1
      return { pageIndex, pageSide };
    };

    const { pageIndex: destinationPageIndex, pageSide: destinationPageSide } = getPageDetails(destination.droppableId);
    const destinationIndexOnSide = destination.index;

    if (destinationIsBlocked(destinationPageIndex, destinationPageSide, destinationIndexOnSide)) return;

    // if current selected component is toc, replace it with tocComponent
    const movedComponent = addUniqueIdentifierToComponent(selectedComponent);

    const newSource = { ...source, index: fromIndex };

    //const sourceClone = Array.from(source);

    // remove component from basicComponents in case it is not a recurring component
    if (!basicComponents[newSource.index]?.recurring) {
      basicComponents.splice(newSource.index, 1);
    }

    //const moveResult = move(basicComponents, selectedComponents, source, destination, movedComponent, bookPages);

    if (!movedComponent.recurring) {
      // remove component from basicComponents in case it is not a recurring component
      setBasicComponents(sortBasicComponents(basicComponents));
    }
  
    const updatedPages = [...bookPages];
    const pageIndex = Math.floor(currentPage / 2);
  
    if (!updatedPages[pageIndex]) {
      updatedPages[pageIndex] = [[], []]; // Initialisiere neue Seite
    }
  
    if (destination.droppableId === `droppable-page-${currentPage}`) {
      updatedPages[pageIndex][0].splice(destination.index, 0, movedComponent); // Linke Seite
    } else if (destination.droppableId === `droppable-page-${currentPage + 1}`) {
      updatedPages[pageIndex][1].splice(destination.index, 0, movedComponent); // Rechte Seite
    }
  
    const balancedPages = balancePageSizes(updatedPages);
    setBookPages(balancedPages);

    setHighlightedComponent(movedComponent);

    // Warte kurz, damit die Komponente in `selectedComponents` aktualisiert ist, dann scrolle hin
    const element = document.getElementById(`selected-${movedComponent.uniqueId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

  };

  const getPageIndexAndSideIndexFromPageNumber = (pageNumber) => {
    const pageIndex = Math.floor((pageNumber) / 2);
    const sideIndex = pageNumber % 2;
    return { pageIndex, sideIndex };
  }

  const destinationIsBlocked = (destinationPageIndex, destinationSide, destinationIndexOnSide) => {
    // Überprüfung, ob die Zielposition in den blockierten Indizes liegt
    const isBlocked = blockedIndexesRef.current.some(index =>
      index.pageIndex === destinationPageIndex &&
      index.sideIndex === destinationSide &&
      index.indexOnSide === destinationIndexOnSide
    );

    if (isBlocked) {
      setAlert({
        severity: "warning",
        message: "Das Verschieben einer Komponente in eine Gruppe ist nicht erlaubt. Bitte platzieren Sie die Komponente entweder vor oder nach der Gruppe."
      });
      return true;
    }
    return false;
  }

  const handleCurrentChoiceToCurrentChoiceDrag = (result) => {
    const { destination } = result;
    const draggedComponent = findComponentInBookPages(result.draggableId.split('uniqueId-')[1], bookPages);
    const updatedPages = [...bookPages];

    // Finde die Quell- und Zielposition innerhalb von bookPages
    const { pageIndex: sourcePageIndex, sideIndex: sourceSide, indexOnSide: sourceIndexOnSide } = getComponentPositionInBookPages(draggedComponent);
    const destinationPageNumber = Number(destination.droppableId.split('-')[2]) - 1;
    const { pageIndex: destinationPageIndex, sideIndex: destinationSide } = getPageIndexAndSideIndexFromPageNumber(destinationPageNumber);
    const destinationIndexOnSide = destination.index;

    if (destinationIsBlocked(destinationPageIndex, destinationSide, destinationIndexOnSide)) return;

    // Wenn eine Gruppenkomponente verschoben wird
    if (draggedComponent.isGroupHeader) {
        const groupAndDuplicates = bookPages.flatMap(page => page.flat())
            .filter(comp => comp.uniqueId === draggedComponent.uniqueId || 
                (Array.isArray(draggedComponent.duplicateIds) && draggedComponent.duplicateIds.includes(comp.uniqueId))
            );

        // Entferne alle Gruppenkomponenten und ihre Duplikate aus bookPages
        groupAndDuplicates.forEach(comp => {
            updatedPages.forEach(page => {
                page.forEach(side => {
                    const index = side.findIndex(item => item.uniqueId === comp.uniqueId);
                    if (index !== -1) {
                        side.splice(index, 1);
                    }
                });
            });
        });

        // Füge alle Gruppenkomponenten an der neuen Position ein
        groupAndDuplicates.forEach((comp, idx) => {
            updatedPages[destinationPageIndex][destinationSide].splice(destination.index + idx, 0, comp);
        });

    } else {
        // Normale Komponente verschieben
        const componentToMoveIndex = sourceIndexOnSide;
        
        if (componentToMoveIndex !== -1) {
            // Entferne die Komponente von der Quellposition
            const [componentToMove] = updatedPages[sourcePageIndex][sourceSide].splice(componentToMoveIndex, 1);
            
            // Prüfe, ob das Ziel eine Gruppenkomponente ist
            const destinationComponent = updatedPages[destinationPageIndex][destinationSide][destinationIndexOnSide];

            if (destinationComponent?.isGroupHeader) {
              // Ziel ist eine Gruppenkomponente, platziere die Komponente vor der Gruppe
              updatedPages[destinationPageIndex][destinationSide].unshift(componentToMove);
            } else {
              // Standardverhalten: Füge die Komponente an der Zielposition ein
              updatedPages[destinationPageIndex][destinationSide].splice(destination.index, 0, componentToMove);
            }
        }
    }

    const balancedPages = balancePageSizes(updatedPages)

    // Aktualisiere bookPages mit den verschobenen Komponenten
    setBookPages(balancedPages);
  };

  const handlePageToPageDrag = (result) => {
    // do not allow to drop in case current page is a blocked page
    if (pageIsBlocked(result)) return;

    const { source, destination } = result;
    const draggedComponent = findComponentInBookPages(result.draggableId.split('uniqueId-')[1], bookPages);

    const getPageDetails = (droppableId) => {
      const pageIndex = Math.floor(parseInt(droppableId.split('-')[2]) / 2);
      const pageSide = parseInt(droppableId.split('-')[2]) % 2 === 0 ? 0 : 1; // Links = 0, Rechts = 1
      return { pageIndex, pageSide };
    };

    const { pageIndex: sourcePageIndex, pageSide: sourcePageSide } = getPageDetails(source.droppableId);
    const { pageIndex: destPageIndex, pageSide: destPageSide } = getPageDetails(destination.droppableId);
    const destinationIndexOnSide = destination.index;

    if (destinationIsBlocked(destPageIndex, destPageSide, destinationIndexOnSide)) return;

    // Erstelle eine tiefe Kopie von bookPages
    const updatedPages = bookPages.map((page) => [
        [...page[0]], // Linke Seite kopieren
        [...page[1]], // Rechte Seite kopieren
    ]);

    // Prüfen, ob das verschobene Element eine Gruppe oder ein Duplikat ist
    const isGroup = draggedComponent.isGroupHeader || draggedComponent.duplicateIds?.length > 0;

    // **1. Suche ALLE zusammengehörigen Komponenten in `bookPages`, nicht nur auf der aktuellen Seite**
    const groupAndDuplicates = bookPages.flatMap((page, pageIdx) =>
        page.flatMap((side, sideIdx) =>
            side.filter((component) =>
                component.uniqueId === draggedComponent.uniqueId ||
                (Array.isArray(draggedComponent.duplicateIds) && draggedComponent.duplicateIds.includes(component.uniqueId))
            ).map(component => ({
                component,
                pageIdx,
                sideIdx,
                indexOnSide: side.indexOf(component)
            }))
        )
    );

    // Falls keine Gruppe vorliegt, einfach die normale Komponente holen
    const movedComponents = isGroup ? groupAndDuplicates.map(g => g.component) : [updatedPages[sourcePageIndex][sourcePageSide][source.index]];

    // **2. Entferne alle Gruppenmitglieder von ihren alten Positionen**
    groupAndDuplicates.forEach(({ pageIdx, sideIdx, component }) => {
        updatedPages[pageIdx][sideIdx] = updatedPages[pageIdx][sideIdx].filter(item => item.uniqueId !== component.uniqueId);
    });

    // **3. Füge die gesamte Gruppe an der neuen Position ein**
    movedComponents.forEach((component, idx) => {
        updatedPages[destPageIndex][destPageSide].splice(destination.index + idx, 0, component);
    });

    // **4. Rufe die Funktion auf, um Seiten neu auszubalancieren**
    const balancedPages = balancePageSizes(updatedPages);
    
    setBookPages(balancedPages);
} ;
  

  const balancePageSizes = (pages) => {
    // Berechnet die Gesamtgröße aller Komponenten auf einer Seite.
    const calculatePageSize = (components) => {
      return components.reduce((total, component) => {
        return total + (Number(componentSizes[component.name]) || 1);
      }, 0);
    };

    //Verschiebt die letzte Komponente zur nächsten Seite.
    const shiftLastComponentToNextPage = (currentPageIndex, currentPageSide) => {
      if (!pages[currentPageIndex + 1]) {
        pages[currentPageIndex + 1] = [[], []]; // Erstelle eine neue Seite, falls sie nicht existiert
      }

      const lastComponent = pages[currentPageIndex][currentPageSide].pop(); // Entferne letzte Komponente der aktuellen Seite

      let nextPageIndex = currentPageIndex;
      let nextPageSide = currentPageSide;

      if (currentPageSide === 0) {
        nextPageSide = 1; // Wechsel zur rechten Seite der gleichen Seite
      } else {
        nextPageIndex++; // Wechsel zur linken Seite der nächsten Seite
        nextPageSide = 0;
      }

      if (lastComponent) {
        // Füge die Komponente an den Anfang der nächsten Seite ein, um die Reihenfolge zu wahren
        pages[nextPageIndex][nextPageSide].unshift(lastComponent);
      }
    };

    // Beginne mit der Größenprüfung und Verschiebung
    let currentPageIndex = 0;
    let currentPageSide = 0;

    while (calculatePageSize(pages[currentPageIndex][currentPageSide]) > 1) {
      shiftLastComponentToNextPage(currentPageIndex, currentPageSide);

      if (currentPageSide === 0) {
        currentPageSide = 1; // Wechsle zur rechten Seite
      } else {
        currentPageIndex++; // Wechsle zur nächsten Seite
        currentPageSide = 0; // Starte auf der linken Seite
      }
    }

    // Zusätzliche Überprüfung für alle Seiten
    for (let i = 0; i < pages.length; i++) {
      for (let side = 0; side <= 1; side++) {
        while (calculatePageSize(pages[i][side]) > 1) {
          shiftLastComponentToNextPage(i, side);
        }
      }
    }

    return pages;
  };

  const nextPage = () => {
    setCurrentPage(prevPage => prevPage + 2); // Umblättern bedeutet zwei Seiten weiter
    
    // Sicherstellen, dass eine neue Doppelseite hinzugefügt wird, wenn diese noch nicht existiert
    const newBookPages = [...bookPages];
    const nextPageIndex = Math.floor((currentPage + 2) / 2);
    let addedNewPage = false;
    
    if (!newBookPages[nextPageIndex]) {
      addedNewPage = true;
      newBookPages[nextPageIndex] = [[], []]; // Neue Doppelseite (linke und rechte Seite) hinzufügen
    }
    
    if (addedNewPage) {
      nextPageTriggerRef.current = true;
      setBookPages(newBookPages);
    }

    setTimeout(() => {
      nextPageTriggerRef.current = false; // Nach dem Rendering wieder zurücksetzen
    }, 0);
  };
  
  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prevPage => prevPage - 2); // Zurückblättern bedeutet zwei Seiten zurück
    }
  };

  const firstPage = () => {
    setCurrentPage(0);
  };

  const lastPage = () => {
    setCurrentPage(bookPages.length * 2 - 2);
  };

  const calculateTotalSize = (components) => {
    return Number(components.reduce((total, component) => total + (Number(component.size) || 1), 0));
  };

  const removeTrailingEmptyPages = () => {
    let lastNonEmptyPageIndex = -1;
  
    // Finde die letzte nicht-leere Doppelseite
    for (let i = 0; i < bookPages.length; i++) {
      if (bookPages[i][0].length > 0 || bookPages[i][1].length > 0) {
        lastNonEmptyPageIndex = i;
      }
    }
  
    // Entferne nur leere Doppelseiten am Ende des Buches
    if (lastNonEmptyPageIndex < bookPages.length - 1) {
      setBookPages(prevBookPages => {
        const newBookPages = prevBookPages.slice(0, lastNonEmptyPageIndex + 1);
  
        // need to call setCurrentPage within setBookPages - otherwise bookPages[Math.floor(currentPage / 2)] for book preview will be undefined
        setCurrentPage(prevPage => {
          const maxValidPage = Math.max(0, lastNonEmptyPageIndex * 2);
          return prevPage > maxValidPage ? maxValidPage : prevPage;
        });
  
        return newBookPages;
      });
    }
  };

  function getPagesCount(bookPages) {
    // define start page to count
    const startCountPage = 1;

    // Letzte belegte physische Seite finden
    let lastOccupiedPage = startCountPage;

    for (let i = 0; i < bookPages.length; i++) {
      const [leftSide, rightSide] = bookPages[i];
      const physicalLeft = i * 2;
      const physicalRight = i * 2 + 1;

      if (leftSide.length > 0) {
        lastOccupiedPage = Math.max(lastOccupiedPage, physicalLeft);
      }
      if (rightSide.length > 0) {
        lastOccupiedPage = Math.max(lastOccupiedPage, physicalRight);
      }
    }

    // Anzahl der gezählten Seiten (inkl. Impressum)
    return lastOccupiedPage - startCountPage + 1;
  }

  const saveBookState = async (cache = true, editWithNewSelection = false) => {
    if (isBookLoading) {
      // Wait until book is loaded and not in the process of being loaded
      return;
    }
    if (!isBookLoaded && !Boolean(isCreating)) {
      // Cannot save book state: book is not loaded or is not in creating mode
      return;
    }

    // remove trailing empty pages
    // if (bookPages.length > 1) {
    //   removeTrailingEmptyPages();
    // }
    const token = localStorage.getItem('authToken');

    try {
      const bookState = {
        pages: bookPages,
        currentPage: currentPage,
        componentSizes: componentSizes,
        basicComponents: basicComponents,
        primaryCategory: primaryPath,
        secondaryCategory: secondaryPath,
        tertiaryCategory: tertiaryPath,
      };

      const response = await axiosInstance.post('/books/save-book-state',
        { isCreating: Boolean(isCreating), bookName, bookId, bookState },
        { headers: { Authorization: `${token}` } }
      );

      if (!cache) {
        setAlert({
          severity: "success",
          message: "Ihr Entwurf wurde erfolgreich gespeichert."
        });

        window.scrollTo({ top: 0, behavior: 'smooth' })

        if (!editWithNewSelection && !cache) {
          setTimeout(() => {
            navigate("/pages/configurator");
          }, 3000);
        }
      }

      // return bookId and bookName in case of editWithNewSelection
      const { bookId: newBookId, bookName: newBookName } = response.data;
      setBookId(newBookId);
      setBookName(newBookName);
      return { success: true, bookId: newBookId, bookName: newBookName };
    } catch (error) {
      setAlert({
        severity: "error",
        message: "Es ist ein Fehler beim Speichern des Entwurfs aufgetreten. Bitte versuchen Sie es erneut oder wenden Sie sich an info@schullogbuch24.de."
      });

      window.scrollTo({ top: 0, behavior: 'smooth' })
      console.error('Fehler beim Speichern des Buchzustands:', error);
    }
  };

  React.useEffect(() => {
    // show alert box only 3 seconds
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 3000); 
  
      return () => clearTimeout(timer);
    }
  }, [alert]);

  React.useEffect(() => {
    if (!isCreating && bookId) {
      setIsBookLoading(true);
      loadBook(bookId);
    }
  }, [bookId]);

  const loadBook = async (bookId) => {
    const token = localStorage.getItem('authToken');
    try {
      const response = await axiosInstance.get(`/books/${bookId}`, {
        headers: { Authorization: `${token}` },
      });
      const bookData = response.data;
  
      // Buchdaten laden
      setBookName(bookData.name || '');
      setBookPages(bookData.pages || [[[], []]]);
      setCurrentPage(bookData.currentPage || 0);
      setComponentSizes(bookData.componentSizes || {});
  
      // Wenn `editWithNewSelection`, basicComponents mit neuen props-Daten laden
      if (location.state?.editWithNewSelection) {
        const { primary, secondaries, tertiaries } = location.state;
        setPrimaryPath(primary);
        setSecondaryPath(secondaries);
        setTertiaryPath(tertiaries);
        const queryParams = new URLSearchParams();
        queryParams.append('secondary', JSON.stringify(secondaries));
        queryParams.append('tertiary', JSON.stringify(tertiaries));
  
        const res = await axiosInstance.get(
          `/components?${queryParams.toString()}`,
          { headers: { Authorization: `${token}` } }
        );
        const filteredComponents = filterBasicComponents(res.data, bookData.pages);
        setBasicComponents(sortBasicComponents(filteredComponents));
      } else {
        setPrimaryPath(bookData.primaryCategory);
        setSecondaryPath(bookData.secondaryCategory);
        setTertiaryPath(bookData.tertiaryCategory);
        const filteredComponents = filterBasicComponents(bookData.basicComponents, bookData.pages);
        setBasicComponents(sortBasicComponents(filteredComponents || []));
      }
      setIsBookLoading(false);
      setIsBookLoaded(true);
    } catch (error) {
      console.error('Fehler beim Laden des Buches:', error);
    }
  };

  const filterBasicComponents = (basicComponents, bookPages) => {
    const usedComponentIds = new Set(
      bookPages.flatMap(page => page.flat()).map(comp => comp._id)
    );
  
    return basicComponents.filter((component) => {
      const isInBookPages = usedComponentIds.has(component._id);
      return !isInBookPages || component.recurring; // Behalte nur, wenn `recurring` true ist
    });
  };  

  const isTocVisibleOnCurrentPage = () => {
    const currentPageIndex = Math.floor(currentPage / 2);
    const currentBookPage = bookPages[currentPageIndex];
    return currentBookPage?.some((side) =>
      side.some((comp) => comp.name.startsWith('Inhaltsverzeichnis'))
    );
  }

  const renderImage = (component) => {
    // Prüfe, ob die Seite existiert und ob das Inhaltsverzeichnis sichtbar ist
    const isTocVisible = isTocVisibleOnCurrentPage();

    // Überspringe das Rendern, wenn die tocComponent nicht sichtbar ist
    if (!isTocVisible && component.name.startsWith('Inhaltsverzeichnis')) {
      return null;
    }

    const imageUrl = component.pngImageUrl
        ? appendTimestamp(component.pngImageUrl)
        : "https://fakeimg.pl/150x150";

    return (
        <img
            src={imageUrl}
            alt={component.name}
            key={imageUrl} // Füge einen key hinzu, um den Render zu erzwingen
            style={{
                width: '100%',
            }}
        />
    );
  };

  const getPositionFromCompToBeDuplicated = (component) => {
    // iterate over bookPages and find position of component to be duplicated
    const updatedPages = [...bookPages];
    let pageIndex = 0;
    let originalPosition = null;
  
    for (let i = 0; i < updatedPages.length; i++) {
      for (let side = 0; side <= 1; side++) {
        const indexOnSide = updatedPages[i][side]?.findIndex(
          (comp) => comp.uniqueId === component.uniqueId
        );
        if (indexOnSide !== -1) {
          pageIndex = i;
          originalPosition = { pageIndex, side, indexOnSide };

          // Setze isGroupHeader: true für die gefundene Komponente
          updatedPages[i][side][indexOnSide] = {
            ...updatedPages[i][side][indexOnSide],
            isGroupHeader: true
          };
          break;
        }
      }
      if (originalPosition) break;
    }
    return { updatedPages, originalPosition };
  }

  const getLastDuplicatePositionFromBookPages = (lastDuplicateId, updatedPages) => {
    let pageIndex = 0;
    let lastDuplicatePosition = null;
    for (let i = 0; i < updatedPages.length; i++) {
      for (let side = 0; side <= 1; side++) {
        const indexOnSide = updatedPages[i][side]?.findIndex(
          (comp) => comp.uniqueId === lastDuplicateId
        );
        if (indexOnSide !== -1) {
          pageIndex = i;
          lastDuplicatePosition = { pageIndex, side, indexOnSide };
          break;
        }
      }
      if (lastDuplicatePosition) break;
    }
    return lastDuplicatePosition;
  }

  const duplicateComponent = (component, count) => {
    const size = Number(componentSizes[component.name]) === 0.33 ? 1/3
      : Number(componentSizes[component.name]) === 0.66 ? 2/3
      : Number(componentSizes[component.name]) || 1;
    let remainingCopies = count;
  
    const { updatedPages, originalPosition } = getPositionFromCompToBeDuplicated(component);
  
    if (!originalPosition) {
      console.warn("Komponente nicht gefunden");
      return;
    }

    // Erstelle ein Array, um die uniqueIds der Duplikate zu speichern
    const newDuplicateIds = [];

    // Bestimme die aktuelle Gruppengröße
    let groupSize = size; // Start mit Originalgröße
    if (component.duplicateIds?.length > 0) {
      groupSize += component.duplicateIds.length * size;
    }

    // Berechne, wie viel Platz mit den neuen Duplikaten benötigt wird
    let requiredSpace = groupSize + (remainingCopies * size);

    // Finde die Startposition für die Duplikate
    let lastDuplicatePosition = originalPosition;
    if (component.isGroupHeader && component.duplicateIds?.length > 0) {
        const lastDuplicateId = component.duplicateIds[component.duplicateIds.length - 1];
        lastDuplicatePosition = getLastDuplicatePositionFromBookPages(lastDuplicateId, updatedPages);
    }

    let pageIndex = lastDuplicatePosition.pageIndex;
    let currentSide = lastDuplicatePosition.side;
    let currentIndex = lastDuplicatePosition.indexOnSide + 1;

    // Stelle sicher, dass genügend Platz vorhanden ist
    while (!hasEnoughSpace(updatedPages, pageIndex, requiredSpace, groupSize)) {
        insertEmptyDoublePage(updatedPages, pageIndex + 1);
    }

    // Nun können wir sicher die Duplikate hinzufügen
    while (remainingCopies > 0) {
        if (!updatedPages[pageIndex]) {
            updatedPages[pageIndex] = [[], []];
        }
  
        const currentPageSide = updatedPages[pageIndex][currentSide];
        let currentSideSize = calculateTotalSize(currentPageSide);
  
        if ((currentSideSize + size) <= 1) {
            let newComponent = { ...component, uniqueId: uuidv4(), isDuplicate: true };
            if (component?.duplicateIds?.length > 0) {
                const { isGroupHeader, duplicateIds, ...rest } = component;
                newComponent = { ...rest, uniqueId: uuidv4(), isDuplicate: true };
            }
            currentPageSide.splice(currentIndex, 0, newComponent);
            newDuplicateIds.push(newComponent.uniqueId);
  
            remainingCopies--;
            currentIndex++;
        } else {
            if (currentSide === 0) {
                currentSide = 1;
                currentIndex = 0;
            } else {
                pageIndex++;
                currentSide = 0;
                currentIndex = 0;
            }
        }
    }

    const originalComponent = updatedPages[originalPosition.pageIndex][originalPosition.side][originalPosition.indexOnSide];
    if (originalComponent) {
        originalComponent.duplicateIds = [...(originalComponent.duplicateIds || []), ...newDuplicateIds];
    }

    setBookPages(updatedPages);
  };

  // Prüft, ob ausreichend Platz für eine Gruppe verfügbar ist
  function hasEnoughSpace(updatedPages, startPage, requiredSpace, size) {
      let availableSpace = size;
      let pageIndex = startPage;

      while (availableSpace <= requiredSpace && pageIndex < updatedPages.length) {
          availableSpace += (1 - calculateTotalSize(updatedPages[pageIndex][0]));
          availableSpace += (1 - calculateTotalSize(updatedPages[pageIndex][1]));
          pageIndex++;
      }

      return availableSpace >= requiredSpace;
  }

  // Fügt eine leere Doppelseite an einer bestimmten Position ein
  function insertEmptyDoublePage(updatedPages, index) {
      updatedPages.splice(index, 0, [[], []]);
  }

  const findComponentPageRange = (component, sliceCount) => {
    let startPage = Infinity;
    let endPage = -Infinity;

    const relevantIds = component.duplicateIds || [];
  
    bookPages.forEach((page, index) => {
      const [leftPage, rightPage] = page;
  
      if (leftPage.some((comp) => relevantIds.includes(comp.uniqueId))) {
        const pageNumber = (index - sliceCount) * 2; // Linke Seite
        startPage = Math.min(startPage, pageNumber);
        endPage = Math.max(endPage, pageNumber);
      }
  
      if (rightPage.some((comp) => relevantIds.includes(comp.uniqueId))) {
        const pageNumber = (index - sliceCount) * 2 + 1; // Rechte Seite
        startPage = Math.min(startPage, pageNumber);
        endPage = Math.max(endPage, pageNumber);
      }
    });
  
    if (startPage === Infinity || endPage === -Infinity) {
      return 'Nicht zugewiesen'; // Falls die Komponente nicht gefunden wurde
    }
  
    return startPage === endPage ? `${startPage}` : `${startPage} - ${endPage}`;
  };

  const sortBasicComponents = (components) => {
    const predefinedOrder = [
      "Grußwort",
      "Schulphilosophie",
      "Hausaufgaben Mo-Fr",
      "Hausaufgaben Mo-Mi",
      "Hausaufgaben Do-Fr",
      "Stundenplan",
      "Krankmeldungen"
    ];
  
    return [...components]
      .map((component) => component.uniqueId ? component : addUniqueIdentifierToComponent(component)) // Sicherstellen, dass uniqueId existiert
      .sort((a, b) => {
        // 1. Priorisiere isBasicComponent=true
        if (a.isBasicComponent && !b.isBasicComponent) return -1;
        if (!a.isBasicComponent && b.isBasicComponent) return 1;
  
        // 2. Innerhalb isBasicComponent=true: Benutzerdefinierte Reihenfolge basierend auf Präfix
        if (a.isBasicComponent && b.isBasicComponent) {
          const indexA = predefinedOrder.findIndex((prefix) => a.name.startsWith(prefix));
          const indexB = predefinedOrder.findIndex((prefix) => b.name.startsWith(prefix));
  
          // Falls beide in der vordefinierten Reihenfolge sind, sortiere entsprechend der Reihenfolge
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
  
          // Falls nur einer in der Reihenfolge ist, sortiere ihn nach oben
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
  
          // Falls keiner in der Reihenfolge ist, alphabetisch sortieren
          return a.name.localeCompare(b.name);
        }
  
        // 3. Der Rest alphabetisch sortieren
        return a.name.localeCompare(b.name);
      });
  };

  const isDragDisabled = (component) => {
    if (component.name.includes("Rückseite")) {
      return true; // Rückseiten dürfen nicht verschoben werden
    } else if(component.name.includes("Deckblatt")) {
      return true; // Deckblätter dürfen nicht verschoben werden
    } else if(component.name.includes("Impressum")) {
      return true; // Deckblätter dürfen nicht verschoben werden
    } else if(component.name.includes("Inhaltsverzeichnis")) {
      return true; // Deckblätter dürfen nicht verschoben werden
    }
    // Duplikate dürfen ebenfalls nicht verschoben werden
    return component.isDuplicate;
  }

  const getBackgroundColor = (component) => {
    let backgroundColor = '#fff';
    if (component.isDuplicate) {
      backgroundColor = '#efefef';
    } else if (highlightedComponent?.uniqueId === component.uniqueId) {
      backgroundColor = 'rgb(71, 145, 219)';
    }
    return backgroundColor;
  }

  const addEmptyDoublePage = (pageIndex, sliceCount) => {
    const realIndex = pageIndex + sliceCount + 1;
    const updatedPages = [...bookPages];
    updatedPages.splice(realIndex, 0, [[], []]);
    nextPageTriggerRef.current = true;
    setBookPages(updatedPages);
    setTimeout(() => {
      nextPageTriggerRef.current = false; // Nach dem Rendering wieder zurücksetzen
    }, 0);
  };

  const doublePageIsEmpty = (pageIndex) => {
    const page = bookPages[pageIndex];
    return page[0].length === 0 && page[1].length === 0;
  }

  const removeDoublePage = (pageIndex) => {
    const updatedPages = [...bookPages];
  
    // Entferne die Doppelseite, wenn sie existiert
    if (updatedPages[pageIndex]) {
      updatedPages.splice(pageIndex, 1);
      nextPageTriggerRef.current = true;
      setBookPages(updatedPages);
      setTimeout(() => {
        nextPageTriggerRef.current = false; // Nach dem Rendering wieder zurücksetzen
      }, 0);
    }
  };

  const getPageBorderColor = (currentPage, isLeftPage) => {
    if (!tocComponent?.allPngImageUrls) return '#42a5f57a';
  
    const length = tocComponent.allPngImageUrls.length;
  
    // Seiten, die Schwarz sein sollen
    const blackPagesLeft = length === 1 ? [0, 2, 4] : length <= 3 ? [0, 2, 4, 6] : [];
    const blackPagesRight = length === 1 ? [0, 2] : length <= 3 ? [0, 2, 4] : [];
  
    return isLeftPage
      ? blackPagesLeft.includes(currentPage) ? '#000' : '#42a5f57a'
      : blackPagesRight.includes(currentPage) ? '#000' : '#42a5f57a';
  };  
  
  const showPageNumber = (currentPage, isLeftPage) => {
    if (!tocComponent?.allPngImageUrls) return null;
  
    const length = tocComponent.allPngImageUrls.length;
    const excludedPagesLeft = length === 1 ? [0, 2, 4] : length <= 3 ? [0, 2, 4, 6] : [];
    const excludedPagesRight = length === 1 ? [0, 2] : length <= 3 ? [0, 2, 4] : [];
  
    const offset = length === 1 ? (isLeftPage ? 4 : 3) : length <= 3 ? (isLeftPage ? 6 : 5) : 0;
  
    return !((isLeftPage ? excludedPagesLeft : excludedPagesRight).includes(currentPage)) ? (
      <span className={isLeftPage ? "leftPageNumber" : "rightPageNumber"}>
        {currentPage - offset}
      </span>
    ) : null;
  };

  const showDropIndicatorMessage = (currentPage, isLeftPage) => {
    if (!tocComponent?.allPngImageUrls) return null;
  
    const length = tocComponent.allPngImageUrls.length;
    const excludedPagesLeft = length === 1 ? [0, 2, 4] : length <= 3 ? [0, 2, 4, 6] : [];
    const excludedPagesRight = length === 1 ? [0, 2] : length <= 3 ? [0, 2, 4] : [];
    const pageSide = isLeftPage ? 0 : 1;
  
      if (!((isLeftPage ? excludedPagesLeft : excludedPagesRight).includes(currentPage)) && (bookPages[Math.floor(currentPage / 2)] || [[], []])[pageSide]?.length === 0) {
        return <Typography
          variant="subtitle2"
          color="textSecondary"
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            opacity: 0.6,
            pointerEvents: "none"
          }}
        >
          Ziehen Sie hier eine Komponente rein
        </Typography>
      }
    return null;
  };

  const shouldHighlight = (currentPage, isLeftPage) => {
    if (!tocComponent?.allPngImageUrls) return null;
  
    const length = tocComponent.allPngImageUrls.length;
    const excludedPagesLeft = length === 1 ? [0, 2, 4] : length <= 3 ? [0, 2, 4, 6] : [];
    const excludedPagesRight = length === 1 ? [0, 2] : length <= 3 ? [0, 2, 4] : [];
  
    if (!((isLeftPage ? excludedPagesLeft : excludedPagesRight).includes(currentPage))) {
      return true;
    }
    return false;
  }

  const getSliceCount = () => {
    const length = tocComponent?.allPngImageUrls?.length || 1;
    return length === 1 ? 2 : length <= 3 ? 3 : 2;
  }

  const getLastTocPagePosition = () => {
    let lastTocPageIndex = -1;
    let lastTocSideIndex = -1;
  
    for (let i = 0; i < bookPages.length; i++) {
      const [left, right] = bookPages[i];
  
      if (left.some((c) => c.name.startsWith("Inhaltsverzeichnis"))) {
        lastTocPageIndex = i;
        lastTocSideIndex = 0; // Linke Seite
      }
      if (right.some((c) => c.name.startsWith("Inhaltsverzeichnis"))) {
        lastTocPageIndex = i;
        lastTocSideIndex = 1; // Rechte Seite (überschreibt linke Seite, falls beides vorhanden)
      }
    }
  
    return { lastTocPageIndex, lastTocSideIndex };
  };

  const getAvailableDropZones = (pageComponents, draggingSize) => {
    // Standardmäßig ist alles frei
    const availableZones = [true, true, true];
  
    // Prüfe belegte Bereiche
    pageComponents.forEach((comp) => {
      if (Number(comp.size) === 1) { // Ganze Seite belegt
        availableZones[0] = false;
        availableZones[1] = false;
        availableZones[2] = false;
      } else if (Number(comp.size) === 0.66) { // 2/3 belegt
        availableZones[0] = false;
        availableZones[1] = false;
      } else if (Number(comp.size) === 0.5) { // 1/2 belegt
        if (availableZones[0] && draggingSize === 0.5) { 
          availableZones[0] = false;
        } else if (availableZones[0] && draggingSize === 0.33) {
          availableZones[0] = false;
          availableZones[1] = false;
        }
      } else if (Number(comp.size) === 0.33) { // 1/3 belegt
        if (availableZones[0]) {
          availableZones[0] = false;
        } else if (availableZones[1]) {
          availableZones[1] = false;
        } else {
          availableZones[2] = false;
        }
      }
    });
  
    // Bestimme die gültigen Drop-Zonen basierend auf der Größe der gedraggten Komponente
    let allowedZones = [];
    if (draggingSize === 1) {
      if (availableZones[0] && availableZones[1] && availableZones[2]) allowedZones = [0];
    } else if (draggingSize === 0.66) {
      if (availableZones[0] && availableZones[1]) allowedZones = [0]; // Obere 2/3
      else if (availableZones[1] && availableZones[2]) allowedZones = [1]; // Untere 2/3
    } else if (draggingSize === 0.5) {
      if (availableZones[0] && availableZones[1]) allowedZones = [0]; // Obere Hälfte
      else if (availableZones[1] && availableZones[2]) allowedZones = [1]; // Untere Hälfte
    } else if (draggingSize === 0.33) {
      if (availableZones[0]) allowedZones.push(0); // Oberes Drittel
      else if (availableZones[1]) allowedZones.push(1); // Mittleres Drittel
      else if (availableZones[2]) allowedZones.push(2); // Unteres Drittel
    }
  
    return allowedZones;
  };

  const getTopPositionForZone = (zone, draggingSize) => {
    if (draggingSize === 1) return "0%"; // Volle Seite → Start bei 0%
    if (draggingSize === 0.66) return zone === 0 ? "0%" : "33.33%"; // 2/3 oben oder mittig
    if (draggingSize === 0.5) return zone === 0 ? "0%" : "50%"; // 1/2 oben oder unten
    if (draggingSize === 0.33) return `${zone * 33.33}%`; // Oberes, mittleres oder unteres Drittel
    return "0%"; // Fallback für unbekannte Größen
  };  

  return (
    <div>
      {alert && (
        <Box sx={{ position: 'absolute', maxWidth: '800px', width: '100%', left: '50%', transform: 'translateX(-50%)', top: '8vh', zIndex: 5}}>
          <Alert severity={alert.severity}>
            {alert.message}
          </Alert>
        </Box>
      )}
      <Grid className="configurator_container" container spacing={1} alignItems="stretch">
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
          <Grid size={{ xs: 4, md: 3 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box className="resultContainer" data-testid="result-container" sx={{ flexGrow: 1, overflow: 'auto' }}>
              <Box className="resultContainerHeader" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff', paddingBottom: '1rem'}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '50px',
                  borderBottom: '2px solid #e0e0e0',
                  marginBottom: "0.5rem",
                  backgroundColor: '#f9f9f9',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  paddingBottom: '14px'
                }}
              >
                <Typography
                  variant="h5" // Etwas kleiner für bessere Integration
                  sx={{
                    color: '#333', // Dezenter Farbton
                    letterSpacing: '0.5px' // Leichte Abstände für mehr Eleganz
                  }}
                >
                  Ihre empfohlenen Komponenten
                </Typography>
              </Box>
                {isPathLoading ? (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="50px"
                    animation="wave"
                    sx={{ mb: '1rem', borderRadius: '8px' }}
                  />
                ) : (
                  <Box
                    sx={{
                      margin: '0 0.5rem',
                      marginBottom: '1rem',
                      padding: '0.5rem',
                      marginLeft: '0.5rem',
                      marginRight: '0.5rem',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      position: 'sticky',
                      top: '0',
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#555',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {`${primaryPath} > ${secondaryPath} > ${tertiaryPath}`}
                    </Typography>
                    {/* <Typography
                      variant="body2"
                      sx={{
                        color: '#555',
                        fontFamily: 'monospace', // Monospace-Schrift für konsistente Ausrichtung
                        whiteSpace: 'pre-wrap', // Zeilenumbrüche erlauben
                      }}
                    >
                      {[
                        primaryPath,
                        ...secondaryPath.map((sec, secIndex) => {
                          const isLastSecondary = secIndex === secondaryPath.length - 1;

                          // Sekundäre Kategorie
                          const secondaryLine = `${isLastSecondary ? '└──' : '├──'} ${sec}`;

                          // Tertiäre Kategorien für diese sekundäre Kategorie
                          const tertiaryLines = tertiaryPath
                            .filter((ter) => ter.secondary === sec) // Nur tertiäre Kategorien für diese sekundäre Kategorie
                            .map((ter, terIndex, filteredTertiary) => {
                              const isLastTertiary = terIndex === filteredTertiary.length - 1;
                              return `${isLastSecondary ? '    ' : '│   '}${isLastTertiary ? '└──' : '├──'} ${ter.tertiary}`;
                            })
                            .join('\n'); // Kombiniere alle tertiären Zeilen mit Zeilenumbrüchen

                          // Kombiniere sekundäre Zeile mit ihren tertiären Zeilen
                          return [secondaryLine, tertiaryLines].filter(Boolean).join('\n');
                        }),
                      ]
                        .filter(Boolean)
                        .map((line) => line.replace(/^│/, '')) // Entferne führende "│" für linksbündige Darstellung
                        .join('\n')}
                    </Typography> */}
                  </Box>
                )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      position: 'sticky', // Sticky Positionierung
                      top: '2rem', // Fixiert an der Oberkante des Containers
                      zIndex: 1, // Stellt sicher, dass die Box über anderen Elementen liegt
                      borderRadius: '8px', // Optional: Runde Ecken
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Suche nach Komponenten..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      style={{
                        width: 'calc(100% - 1rem)',
                        padding: '8px 32px 8px 8px', // Platz für das "x" rechts
                        border: '1px solid #ddd',
                        margin: '0 0.5rem',
                        borderRadius: '8px',
                        fontSize: '1rem',
                      }}
                    />
                    {searchText && (
                      <span
                        onClick={() => setSearchText('')} // Eingabe leeren
                        style={{
                          position: 'absolute',
                          right: '130px',
                          top: '5px',
                          cursor: 'pointer',
                          color: '#aaa',
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                          userSelect: 'none', // Verhindert Textauswahl
                        }}
                      >
                        ×
                      </span>
                    )}
                    <Select
                      multiple
                      displayEmpty
                      value={selectedClasses}
                      onChange={(e) => setSelectedClasses(e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <em style={{ color: 'rgb(117, 117, 117)' }}>Klassen</em>;
                        }
                        return selected.join(', ');
                      }}
                      style={{
                        maxWidth: '120px',
                        borderRadius: '8px',
                        marginRight: '0.5rem',
                        height: '36.5px'
                      }}
                    >
                      {Object.keys(classColors).map((cls) => (
                        <MenuItem key={cls} value={cls} sx={{ height: '40px'}}>
                          <Checkbox
                            icon={<RadioButtonUncheckedIcon sx={{ color: classColors[cls] }} />}
                            checkedIcon={<CheckCircleIcon sx={{ color: classColors[cls] }} />}
                            checked={selectedClasses.includes(cls)}
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 22, // Größe anpassen
                              }
                            }}
                          />
                          <ListItemText primary={cls} />
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  </Box>
                  <Droppable droppableId="droppable-list">
                    {(provided) => (
                      <List
                        dense={true}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          display: 'flex',
                          flexDirection: 'column', // Liste in einer Spalte
                          alignItems: 'flex-start', // Elemente linksbündig
                          width: 'calc(100% - 1rem)', // Volle Breite der Liste
                          margin: '0 0.5rem'
                        }}
                      >
                        {isBasicComponentsLoading ? (
                          Array.from({ length: 5 }).map((_, index) => (
                            <Skeleton
                              key={`skeleton-list-item-${index}`}
                              variant="rectangular"
                              width="100%"
                              height="48px"
                              animation="wave"
                              sx={{ mb: '8px', borderRadius: '8px' }}
                            />
                          ))
                        ) : (
                          (() => {
                            const filteredComponents = basicComponents.filter((component) => {
                              const nameMatches = component.name.toLowerCase().includes(searchText.toLowerCase());
                              const classMatches = selectedClasses.length === 0 || selectedClasses.includes(component.class);
                              return nameMatches && classMatches;
                            });                            
                            return filteredComponents.map((basicComponent, index) => (
                              <Draggable
                                key={`draggable-basicComponentList-${basicComponent.name}`}
                                draggableId={`draggable-basicComponentList-${basicComponent.name}`}
                                index={index}
                              >
                                {(provided, snapshot) => {
                                  const isExpanded = !snapshot.isDragging && ((expandedAccordionBasic === null && index === 0) || expandedAccordionBasic === basicComponent.uniqueId);

                                  return (
                                    <Accordion
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      expanded={isExpanded}
                                      onChange={() =>
                                        setExpandedAccordionBasic(isExpanded ? null : basicComponent.uniqueId)
                                      }
                                      style={{
                                        ...provided.draggableProps.style,
                                        margin: 0,
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        marginBottom: '8px',
                                        boxShadow: snapshot.isDragging
                                          ? '0 4px 10px rgba(0, 0, 0, 0.3)'
                                          : '0 2px 5px rgba(0, 0, 0, 0.1)',
                                        backgroundColor: '#fff',
                                        width: snapshot.isDragging ? '300px' : '100%',
                                        fontSize: '5px',
                                        height: snapshot.isDragging ? "48px" : "auto",
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        {...provided.dragHandleProps}
                                        sx={{
                                          alignItems: 'center',
                                          backgroundColor: classColorsLight[basicComponent.class] || 'transparent',
                                          borderTopLeftRadius: '8px',
                                          borderRightLeftRadius: '8px',
                                          borderBottomRightRadius: isExpanded ? '0px' : '8px',
                                          borderBottomLeftRadius: isExpanded ? '0px' : '8px',
                                        }}
                                      >
                                        {/* <CheckBoxOutlineBlankIcon style={{ color: '#757575', marginRight: '8px' }} /> */}
                                        <ListItemText primary={basicComponent.name} data-testid={`result-item-${index}`} />
                                        {basicComponent.recurring && getComponentCount(basicComponent) !== 0 && (
                                          <Tooltip title="Gesamtanzahl verwendet">
                                            <Chip
                                              label={`x${getComponentCount(basicComponent)}`}
                                              color="secondary"
                                              size="small"
                                              sx={{
                                                marginRight: '0.25rem',
                                                marginTop: '0.25rem',
                                                fontSize: '0.75rem',
                                                height: '20px',
                                                bgcolor: '#abaaaa'
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                        {basicComponent.isBasicComponent && (
                                            <Tooltip title="Basiskomponente">
                                              <Chip
                                                label="Basic"
                                                color="primary"
                                                size="small"
                                                sx={{
                                                  marginRight: '0.25rem',
                                                  marginTop: '0.25rem',
                                                  fontSize: '0.75rem',
                                                  height: '20px',
                                                  bgcolor: 'primary.light'
                                                }}
                                              />
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Seitengröße">
                                          <Chip
                                            label={
                                              {
                                                "1": "1",
                                                "0.33": "1/3",
                                                "0.5": "1/2",
                                                "0.66": "2/3"
                                              }[String(basicComponent.size)] || "N/A"
                                            }
                                            color="success"
                                            size="small"
                                            sx={{
                                              marginRight: '0.25rem',
                                              marginTop: '0.25rem',
                                              minWidth: '35px',
                                              fontSize: '0.75rem',
                                              height: '20px',
                                              bgcolor: 'success.light'
                                            }}
                                          />
                                        </Tooltip>
                                      </AccordionSummary>
                                      <AccordionDetails sx={{backgroundColor: classColorsLight[basicComponent.class] || 'transparent', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px'}}>
                                        <Typography variant="body2">{basicComponent.description}</Typography>
                                      </AccordionDetails>
                                    </Accordion>
                                  );
                                }}
                              </Draggable>
                            ));
                          })()
                        )}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </Box>
              </Grid>
              <Grid className="schullogbuchPreview" size={{ xs: 8, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Grid container spacing={1} sx={{ flexGrow: 1, maxWidth: 'calc(2 * (100vh / 1.414))' }}>
              <Grid size={{ xs: 12, md: 12}}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: '50px',
                  borderBottom: '2px solid #e0e0e0',
                  marginBottom: "0.5rem",
                  backgroundColor: '#f9f9f9',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                }}
              >
                <Typography
                  variant="h5" // Etwas kleiner für bessere Integration
                  sx={{
                    color: '#333', // Dezenter Farbton
                    letterSpacing: '0.5px' // Leichte Abstände für mehr Eleganz
                  }}
                >
                  Ihre Schullogbuch Vorschau
                </Typography>
                <Typography variant="h6" fontStyle={'italic'}>
                  {bookName || ''}
                </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Paper 
                className="page1" 
                variant="outlined" 
                elevation={5}
                sx={{
                  borderColor: getPageBorderColor(currentPage, true)
                }}
              >
                {currentPage === 0 && <Box className="ribbon left">Rückseite</Box>}
                {currentPage === 2 && <Box className="ribbon left">Impressum</Box>}
                {currentPage === 4 && <Box className="ribbon left">Inhaltsverzeichnis</Box>}
                {currentPage === 6 && bookPages.length > 3 && bookPages[3][0].some(comp => comp.name.startsWith("Inhaltsverzeichnis")) && (<Box className="ribbon left">Inhaltsverzeichnis</Box>)}

                  <Droppable droppableId={`droppable-page-${currentPage}`}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      id="dropzone"
                      {...provided.droppableProps}
                      className={`droppableArea ${
                        (snapshot.isDraggingOver && draggingComponent && shouldHighlight(currentPage, true)) ? "dropzone-highlight" : ""
                      }`}
                      sx={{
                        position: "absolute", // Passt sich an die Größe von Paper an
                        top: 0,
                        left: 0,
                        width: "100%", // Gleiche Breite wie Paper
                        height: "100%", // Gleiche Höhe wie Paper
                        overflow: "hidden", // Kein ungewolltes Scrollen
                        backgroundColor: (snapshot.isDraggingOver && draggingComponent && shouldHighlight(currentPage, true)) ? "rgba(76, 175, 80, 0.1)" : "transparent",
                        transition: "border 0.2s ease, background-color 0.2s ease"
                      }}
                    >
                      {showDropIndicatorMessage(currentPage, true)}
                      {snapshot.isDraggingOver && draggingComponent && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "10px",
                            left: "50%", // Zentriert die Box horizontal
                            transform: "translateX(-50%)", // Korrigiert die Verschiebung
                            width: "90%",
                            height: "calc(100% - 15px)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pointerEvents: "none",
                            gap: "5px", // Abstand zwischen den Vorschau-Boxen
                          }}
                        >
                          {shouldHighlight(currentPage, true) && (() => {
                            const pageComponents = (bookPages[Math.floor(currentPage / 2)] || [[], []])[0] || [];
                            const allowedZones = getAvailableDropZones(pageComponents, Number(draggingComponent.size));

                            const spacing = 5; // Abstand zwischen den Vorschau-Boxen in px
                            const sizePercentageMap = {
                              1: 95,    // Ganze Seite
                              0.66: 60, // Zwei Drittel der Seite
                              0.5: 45,   // Hälfte der Seite
                              0.33: 28 // Ein Drittel der Seite
                            };

                            return allowedZones.map((zone, index) => (
                              <Box
                                key={`drop-preview-${index}`}
                                sx={{
                                  position: "absolute",
                                  top: getTopPositionForZone(zone, Number(draggingComponent.size)),
                                  width: "100%",
                                  height: `${sizePercentageMap[draggingComponent.size] || 100}%`, // Passt die Höhe an, um den Abstand zu berücksichtigen
                                  backgroundColor: "rgba(0, 150, 255, 0.2)",
                                  border: "1px dashed rgba(0,0,0,0.5)",
                                  padding: "10px",
                                  opacity: 0.7,
                                  marginBottom: index !== allowedZones.length - 1 ? `${spacing}px` : "0", // Abstand außer bei der letzten Box
                                }}
                              />
                            ));
                          })()}
                        </Box>
                      )}
                        {/* Gewählte Komponenten */}
                        <List dense={true}>
                          {(bookPages[Math.floor(currentPage / 2)] || [[], []])[0]?.map((selectedComponent, index) => (
                            <Draggable
                              key={`page-${currentPage}-${selectedComponent.name}-${index}-uniqueId-${selectedComponent.uniqueId}`}
                              draggableId={`${selectedComponent.name}-left-${index}-uniqueId-${selectedComponent.uniqueId}`}
                              index={index}
                              isDragDisabled={isDragDisabled(selectedComponent)}
                            >
                              {(provided) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    height: componentSizes[selectedComponent.name] === "0.33" ? '32%' :
                                            componentSizes[selectedComponent.name] === "0.5" ? '48%' :
                                            componentSizes[selectedComponent.name] === "0.66" ? '65%' : '98%',
                                    alignItems: 'flex-start',
                                    padding: '0px',
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  {renderImage(selectedComponent)}
                                </ListItem>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </List>
                      </Box>
                    )}
                  </Droppable>
                  {showPageNumber(currentPage, true)}
                  <IconButton className="nav-button first" onClick={firstPage} sx={{ fontSize: '2.5rem', p: 0}}>
                    <FirstPageIcon fontSize='1.5rem'/>
                  </IconButton>
                  <IconButton className="nav-button left" onClick={prevPage} sx={{ fontSize: '2.5rem', p: 0 }}>
                    <NavigateBeforeIcon fontSize="1.5rem"/>
                  </IconButton>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Paper
                  className="page2"
                  variant="outlined"
                  elevation={5}
                  sx={{
                    borderColor: getPageBorderColor(currentPage, false)
                  }}
                >
                {currentPage === 0 && (<Box className="ribbon right">Deckblatt</Box>)}
                {currentPage === 4 && bookPages[2][1].some(comp => comp.name.startsWith("Inhaltsverzeichnis")) && (<Box className="ribbon right">Inhaltsverzeichnis</Box>)}
                  <Droppable droppableId={`droppable-page-${currentPage + 1}`}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      id="dropzone"
                      {...provided.droppableProps}
                      className={`droppableArea ${
                        (snapshot.isDraggingOver && draggingComponent && shouldHighlight(currentPage, false)) ? "dropzone-highlight" : ""
                      }`}
                      sx={{
                        position: "absolute", // Passt sich an die Größe von Paper an
                        top: 0,
                        left: 0,
                        width: "100%", // Gleiche Breite wie Paper
                        height: "100%", // Gleiche Höhe wie Paper
                        overflow: "hidden", // Kein ungewolltes Scrollen
                        backgroundColor: (snapshot.isDraggingOver && draggingComponent && shouldHighlight(currentPage, false)) ? "rgba(76, 175, 80, 0.1)" : "transparent",
                        transition: "border 0.2s ease, background-color 0.2s ease"
                      }}
                    >
                      {showDropIndicatorMessage(currentPage, false)}
                      {snapshot.isDraggingOver && draggingComponent && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "10px",
                            left: "50%", // Zentriert die Box horizontal
                            transform: "translateX(-50%)", // Korrigiert die Verschiebung
                            width: "90%",
                            height: "calc(100% - 15px)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pointerEvents: "none",
                            gap: "5px", // Abstand zwischen den Vorschau-Boxen
                          }}
                        >
                          {shouldHighlight(currentPage, false) && (() => {
                            const pageComponents = (bookPages[Math.floor(currentPage / 2)] || [[], []])[1] || [];
                            const allowedZones = getAvailableDropZones(pageComponents, Number(draggingComponent.size));

                            const spacing = 5; // Abstand zwischen den Vorschau-Boxen in px
                            const sizePercentageMap = {
                              1: 95,    // Ganze Seite
                              0.66: 60, // Zwei Drittel der Seite
                              0.5: 45,   // Hälfte der Seite
                              0.33: 28 // Ein Drittel der Seite
                            };

                            return allowedZones.map((zone, index) => (
                              <Box
                                key={`drop-preview-${index}`}
                                sx={{
                                  position: "absolute",
                                  top: getTopPositionForZone(zone, Number(draggingComponent.size)),
                                  width: "100%",
                                  height: `${sizePercentageMap[draggingComponent.size] || 100}%`, // Passt die Höhe an, um den Abstand zu berücksichtigen
                                  backgroundColor: "rgba(0, 150, 255, 0.2)",
                                  border: "1px dashed rgba(0,0,0,0.5)",
                                  padding: "10px",
                                  opacity: 0.7,
                                  marginBottom: index !== allowedZones.length - 1 ? `${spacing}px` : "0", // Abstand außer bei der letzten Box
                                }}
                              />
                            ));
                          })()}
                        </Box>
                      )}
                        {/* Gewählte Komponenten für die rechte Seite */}
                        <List dense={true}>
                        {(bookPages[Math.floor(currentPage / 2)] || [[], []])[1]?.map((selectedComponent, index) => (
                          <Draggable
                          key={`page-${currentPage + 1}-${selectedComponent.name}-${index}-uniqueId-${selectedComponent.uniqueId}`}
                            draggableId={`${selectedComponent.name}-right-${index}-uniqueId-${selectedComponent.uniqueId}`}
                            index={index}
                            isDragDisabled={isDragDisabled(selectedComponent)}
                          >
                            {(provided) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  height: componentSizes[selectedComponent.name] === "0.33" ? '32%' :
                                          componentSizes[selectedComponent.name] === "0.5" ? '48%' :
                                          componentSizes[selectedComponent.name] === "0.66" ? '65%' : '98%',
                                  alignItems: 'flex-start',
                                  padding: '0px',
                                  ...provided.draggableProps.style,
                                }}
                              >
                                {renderImage(selectedComponent)}
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}  {/* Das Placeholder-Element ist wichtig */}
                      </List>
                      </Box>
                    )}
                  </Droppable>
                  {/* <span className="rightPageNumber"> Seite {currentPage + 2} </span> */}
                  {showPageNumber(currentPage, false)}
                  <IconButton className="nav-button right" onClick={nextPage} sx={{ fontSize: '2.5rem', p: 0 }}>
                    <NavigateNextIcon fontSize="1.5rem"/>
                  </IconButton>
                  <IconButton className="nav-button last" onClick={lastPage} sx={{ fontSize: '2.5rem', p: 0 }}>
                    <LastPageIcon fontSize="1.5rem"/>
                  </IconButton>
                </Paper>
              </Grid>
              </Grid>
              </Grid>
              <Grid size={{ xs: 4, md: 3 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box className="currentChoice" sx={{ flexGrow: 1, overflow: 'auto', padding: 0}}>
                  <Box className="currentChoiceHeader" sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '50px',
                      borderBottom: '2px solid #e0e0e0',
                      marginBottom: "0.5rem",
                      backgroundColor: '#f9f9f9',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      paddingBottom: '14px'
                    }}
                  >
                    <Typography
                      variant="h5" // Etwas kleiner für bessere Integration
                      sx={{
                        color: '#333', // Dezenter Farbton
                        letterSpacing: '0.5px' // Leichte Abstände für mehr Eleganz
                      }}
                    >
                      Ihre aktuelle Auswahl
                    </Typography>
                    </Box>
                    {/* <Typography variant="h5" textAlign="center" marginBottom="0.5rem">Ihre aktuelle Auswahl</Typography> */}
                  </Box>
                      <List sx={{ width: '99%'}} dense={true}>
                        {bookPages.slice(getSliceCount()).map((doublePage, i) => {
                          const { lastTocPageIndex, lastTocSideIndex } = getLastTocPagePosition();

                          // Bestimmen, ab welcher physischen Seite die Nummerierung beginnt
                          const pageOffset = lastTocPageIndex === -1 ? 4 : (lastTocPageIndex * 2 + lastTocSideIndex + 1);
                          
                          const realLeftSideNumber = i * 2;
                          const realRightSideNumber = realLeftSideNumber + 1;
                          const leftSideNumber = realLeftSideNumber - pageOffset;
                          const rightSideNumber = leftSideNumber + 1;

                          return (
                            <Box
                              key={`doublePage-${i}`}
                              sx={{
                                marginBottom: '24px',
                                margin: '0 0.5rem 24px 0.5rem',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                padding: '10px'
                              }}
                            >
                              {doublePageIsEmpty(i + getSliceCount()) && (
                                <Tooltip title="Diese Doppelseite entfernen">
                                <IconButton
                                  onClick={() => removeDoublePage(i + getSliceCount())}
                                  sx={{ position: "absolute", mt: '-15px', right: '-10px', zIndex: 1, color: "error.light", "--IconButton-hoverBg": "transparent" }}
                                >
                                  <RemoveCircleIcon
                                    sx={{
                                      color: "#abaaaa",
                                      fontSize: '20px',
                                      '&:hover': {
                                        color: 'error.light',
                                        backgroundColor: 'transparent',
                                        "--IconButton-hoverBg": "transparent",
                                      }
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                              )}
                              {/*
                                ============ LINKES SEITEN-Droppable ============
                              */}
                              {leftSideNumber + pageOffset !== 0 && (
                              <Droppable droppableId={`droppable-currentChoice-${realLeftSideNumber + pageOffset}`} >
                                {(providedLeft, snapshot) => (
                                  <Box
                                  className={` ${
                                    (shouldHighlightCurrentChoice && snapshot.isDraggingOver) ? "dropzone-highlight" : ""
                                  }`}
                                    ref={providedLeft.innerRef}
                                    {...providedLeft.droppableProps}
                                    sx={{
                                      border: '1px solid #42a5f57a',
                                      borderRadius: '8px',
                                      marginBottom: '16px',
                                      padding: '8px',
                                      position: 'relative',
                                      minHeight: '60px',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: '-2px',
                                        left: '8px',
                                        backgroundColor: 'white',
                                        transform: 'translateY(-50%)',
                                        fontSize: '12px',
                                      }}
                                    >
                                      Seite {leftSideNumber + pageOffset}
                                    </Box>

                                    {doublePage[0].map((component, index) => (
                                      <Draggable
                                      key={`draggable-selectedComponentList-uniqueId-${component.uniqueId}`}
                                      draggableId={`draggable-selectedComponentList-uniqueId-${component.uniqueId}`}
                                      index={index}
                                      isDragDisabled={isDragDisabled(component)} // Draggen deaktivieren, wenn es sich um ein Duplikat handelt
                                    >
                                      {(provided, snapshot) => {
                                        const isExpanded = !snapshot.isDragging && expandedAccordionCurrentChoice === component.uniqueId;
                                        return (
                                          <Accordion
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            expanded={isExpanded}
                                            onChange={() =>
                                              setExpandedAccordionCurrentChoice(isExpanded ? null : component.uniqueId)
                                            }
                                            onClick={() => setCurrentPage(realLeftSideNumber + pageOffset)}
                                            style={{
                                              ...provided.draggableProps.style,
                                              margin: 0,
                                              border: '1px solid #ddd',
                                              borderRadius: '8px',
                                              marginBottom: '8px',
                                              boxShadow: snapshot.isDragging
                                                ? '0 4px 10px rgba(0, 0, 0, 0.3)'
                                                : '0 2px 5px rgba(0, 0, 0, 0.1)',
                                              backgroundColor: getBackgroundColor(component),
                                              //transition: 'background-color 3s ease-out', TODO: Do not remove the comment, since this is causing to stuck the element
                                              // see: https://github.com/hello-pangea/dnd/issues/709
                                              width: snapshot.isDragging ? '300px' : '100%',
                                              height: snapshot.isDragging ? component.recurring ? "80px" : "48px" : "auto",
                                            }}
                                          >
                                            <AccordionSummary
                                              expandIcon={<ExpandMoreIcon />}
                                              id={`selected-${component.uniqueId}`}
                                              {...provided.dragHandleProps}
                                              sx={{
                                                '.MuiAccordionSummary-content': {
                                                  display: 'flex',
                                                  flexDirection: 'row',    // alles in einer Zeile
                                                  alignItems: 'center',
                                                  width: '100%',
                                                  backgroundColor: getBackgroundColor(component),
                                                  //transition: 'background-color 3s ease-out', TODO: Do not remove the comment, since this is causing to stuck the element
                                                  // https://github.com/hello-pangea/dnd/issues/709
                                                  justifyContent: 'space-between',
                                                },
                                                '&.Mui-focusVisible': {
                                                  backgroundColor: '#fff',
                                                }
                                              }}
                                            >
                                              <ListItemText
                                                sx={{
                                                  flexGrow: 1,
                                                  margin: 0,
                                                }}
                                                primary={
                                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box sx={{flexGrow: 1}}>{component.name}</Box>
                                                    
                                                    <Box sx={{ display: 'flex'}}>
                                                    {component.isGroupHeader && (
                                                      <Tooltip title="Gruppe">
                                                        <Chip
                                                          label="Group"
                                                          color="secondary"
                                                          size="small"
                                                          sx={{
                                                            marginRight: '0.25rem',
                                                            minWidth: '35px',
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            paddingTop: '1px',
                                                            bgcolor: 'secondary.light'
                                                          }}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                    {component.recurring && (
                                                      <Tooltip title="Gesamtanzahl verwendet">
                                                        <Chip
                                                          label={`x${getComponentCount(component)}`}
                                                          color="secondary"
                                                          size="small"
                                                          sx={{
                                                            marginRight: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            paddingTop: '1px',
                                                            bgcolor: '#abaaaa'
                                                          }}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                    {/* {item.isBasicComponent && (
                                                      <Tooltip title="Basiskomponente">
                                                        <Chip
                                                          label="Basic"
                                                          color="primary"
                                                          size="small"
                                                          sx={{
                                                            mr: '3px',
                                                            minWidth: '35px',
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            paddingTop: '1px',
                                                            bgcolor: 'primary.light'
                                                          }} />
                                                      </Tooltip>
                                                    )} */}
                                                    <Tooltip title="Seitengröße">
                                                      <Chip
                                                        label={{
                                                          "1": "1",
                                                          "0.33": "1/3",
                                                          "0.5": "1/2",
                                                          "0.66": "2/3"
                                                        }[String(component.size)] || "N/A"}
                                                        color="success"
                                                        size="small"
                                                        sx={{
                                                          mr: '3px',
                                                          minWidth: '35px',
                                                          fontSize: '0.75rem',
                                                          height: '20px',
                                                          paddingTop: '1px',
                                                          bgcolor: 'success.light'
                                                        }}
                                                      />
                                                    </Tooltip>
                                                    {(!component.recurring && !component.isGroupHeader && !component.isDuplicate && !component.name.includes("Rückseite") && !component.name.includes("Deckblatt")) &&  (
                                                      <Tooltip title="Entfernen von Auswahl">
                                                        <IconButton
                                                          edge="end"
                                                          aria-label="remove"
                                                          color="error"
                                                          onClick={(e) => { e.stopPropagation(); removeFromCurrentChoice(component); }}
                                                          sx={{
                                                            padding: 0,
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            marginRight: '3px',
                                                            color: "#abaaaa",
                                                            '&:hover': {
                                                              color: 'error.light',
                                                              backgroundColor: 'transparent',
                                                            }
                                                          }}
                                                        >
                                                          <DeleteIcon />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                    </Box>
                                                  </Box>
                                                }
                                                secondaryTypographyProps={{
                                                  component: 'div',
                                                  sx: { width: '100%' }
                                                }}
                                                secondary={
                                                  (!component.isDuplicate && (component.isGroupHeader || component.recurring)) && (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minHeight: '30px' }}>
                                                    {/* Linke Seite (Text & Input) */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      {(component.recurring && component.isGroupHeader) &&
                                                        `Seite ${findComponentPageRange(component, getSliceCount())}, Anzahl: ${component.duplicateIds?.length + 1}`
                                                      }
                                                
                                                      {/* Input Feld + Icon */}
                                                      {component.recurring && !component.isDuplicate && (
                                                        <>
                                                          <input
                                                            type="number"
                                                            min="1"
                                                            defaultValue={component.duplicateIds?.length || 1}
                                                            id={`duplicate-input-${component.uniqueId}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{
                                                              width: '50px',
                                                              padding: '2px',
                                                              textAlign: 'center',
                                                              borderRadius: '4px',
                                                              border: '1px solid #ddd',
                                                            }}
                                                          />
                                                          <Tooltip title="Duplizieren">
                                                            <IconButton
                                                              edge="end"
                                                              aria-label="duplicate"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const inputElement = document.getElementById(`duplicate-input-${component.uniqueId}`);
                                                                const duplicateAmount = parseInt(inputElement.value, 10);
                                                                if (duplicateAmount > 0) {
                                                                  duplicateComponent(component, duplicateAmount);
                                                                }

                                                                // reset value to 1 after duplicate
                                                                setTimeout(() => {
                                                                  inputElement.value = "1";
                                                                }, 0);
                                                              }}
                                                              sx={{
                                                                color: "#abaaaa",
                                                                fontSize: '20px',
                                                                '&:hover': {
                                                                  color: 'success.light',
                                                                  backgroundColor: 'transparent',
                                                                }
                                                              }}
                                                            >
                                                              <LibraryAddIcon />
                                                            </IconButton>
                                                          </Tooltip>
                                                        </>
                                                      )}
                                                    </Box>
                                                
                                                    {/* Rechte Seite: DeleteIcon */}
                                                    {!component.isDuplicate && (component.recurring || component.isGroupHeader) &&  (
                                                      <Tooltip title="Entfernen von Auswahl">
                                                        <IconButton
                                                          edge="end"
                                                          aria-label="remove"
                                                          color="error"
                                                          onClick={(e) => { e.stopPropagation(); removeFromCurrentChoice(component); }}
                                                          sx={{
                                                            padding: 0,
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            marginRight: '3px',
                                                            color: "#abaaaa",
                                                            '&:hover': {
                                                              color: 'error.light',
                                                              backgroundColor: 'transparent',
                                                            }
                                                          }}
                                                        >
                                                          <DeleteIcon />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                  </Box>
                                                  )
                                                }                                            
                                              />
                                            </AccordionSummary>
                                    
                                            <AccordionDetails>
                                              <Typography variant="body2">{component.description}</Typography>
                                            </AccordionDetails>
                                          </Accordion>
                                        );
                                      }}
                                    </Draggable>
                                    ))}

                                    {providedLeft.placeholder}
                                  </Box>
                                )}
                              </Droppable>
                              )}

                              {/*
                                ============ RECHTES SEITEN-Droppable ============
                              */}
                              <Droppable droppableId={`droppable-currentChoice-${realRightSideNumber + pageOffset}`} >
                                {(providedRight, snapshot) => (
                                  <Box
                                    className={` ${
                                      (shouldHighlightCurrentChoice && snapshot.isDraggingOver) ? "dropzone-highlight" : ""
                                    }`}
                                    ref={providedRight.innerRef}
                                    {...providedRight.droppableProps}
                                    sx={{
                                      border: '1px solid #42a5f57a',
                                      borderRadius: '8px',
                                      padding: '8px',
                                      position: 'relative',
                                      minHeight: '60px',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: '-2px',
                                        left: '8px',
                                        backgroundColor: 'white',
                                        transform: 'translateY(-50%)',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Seite {rightSideNumber + pageOffset}
                                    </Box>

                                    {doublePage[1].map((component, index) => (
                                      <Draggable
                                      key={`draggable-selectedComponentList-uniqueId-${component.uniqueId}`}
                                      draggableId={`draggable-selectedComponentList-uniqueId-${component.uniqueId}`}
                                      index={index}
                                      isDragDisabled={isDragDisabled(component)} // Draggen deaktivieren, wenn es sich um ein Duplikat handelt
                                    >
                                      {(provided, snapshot) => {
                                        const isExpanded = !snapshot.isDragging && expandedAccordionCurrentChoice === component.uniqueId;
                                        return (
                                          <Accordion
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            expanded={isExpanded}
                                            onChange={() =>
                                              setExpandedAccordionCurrentChoice(isExpanded ? null : component.uniqueId)
                                            }
                                            onClick={() => setCurrentPage(realLeftSideNumber + pageOffset)}
                                            style={{
                                              ...provided.draggableProps.style,
                                              margin: 0,
                                              border: '1px solid #ddd',
                                              borderRadius: '8px',
                                              marginBottom: '8px',
                                              boxShadow: snapshot.isDragging
                                                ? '0 4px 10px rgba(0, 0, 0, 0.3)'
                                                : '0 2px 5px rgba(0, 0, 0, 0.1)',
                                              backgroundColor: getBackgroundColor(component),
                                              //transition: 'background-color 3s ease-out', TODO: Do not remove the comment, since this is causing to stuck the element
                                              // see: https://github.com/hello-pangea/dnd/issues/709
                                              width: snapshot.isDragging ? '300px' : '100%',
                                              height: snapshot.isDragging ? component.recurring ? "80px" : "48px" : "auto",
                                            }}
                                          >
                                            <AccordionSummary
                                              expandIcon={<ExpandMoreIcon />}
                                              id={`selected-${component.uniqueId}`}
                                              {...provided.dragHandleProps}
                                              sx={{
                                                '.MuiAccordionSummary-content': {
                                                  display: 'flex',
                                                  flexDirection: 'row',    // alles in einer Zeile
                                                  alignItems: 'center',
                                                  width: '100%',
                                                  backgroundColor: getBackgroundColor(component),
                                                  //transition: 'background-color 3s ease-out', TODO: Do not remove the comment, since this is causing to stuck the element
                                                  // https://github.com/hello-pangea/dnd/issues/709
                                                  justifyContent: 'space-between',
                                                },
                                                '&.Mui-focusVisible': {
                                                  backgroundColor: '#fff',
                                                }
                                              }}
                                            >
                                              <ListItemText
                                                sx={{
                                                  flexGrow: 1,
                                                  margin: 0,
                                                }}
                                                primary={
                                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box sx={{flexGrow: 1}}>{component.name}</Box>
                                                    
                                                    <Box sx={{ display: 'flex'}}>
                                                    {component.isGroupHeader && (
                                                      <Tooltip title="Gruppe">
                                                        <Chip
                                                          label="Group"
                                                          color="secondary"
                                                          size="small"
                                                          sx={{
                                                            marginRight: '0.25rem',
                                                            minWidth: '35px',
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            paddingTop: '1px',
                                                            bgcolor: 'secondary.light'
                                                          }}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                    {component.recurring && (
                                                      <Tooltip title="Gesamtanzahl verwendet">
                                                        <Chip
                                                          label={`x${getComponentCount(component)}`}
                                                          color="secondary"
                                                          size="small"
                                                          sx={{
                                                            marginRight: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            paddingTop: '1px',
                                                            bgcolor: '#abaaaa'
                                                          }}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                    {/* {item.isBasicComponent && (
                                                      <Tooltip title="Basiskomponente">
                                                        <Chip
                                                          label="Basic"
                                                          color="primary"
                                                          size="small"
                                                          sx={{
                                                            mr: '3px',
                                                            minWidth: '35px',
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            paddingTop: '1px',
                                                            bgcolor: 'primary.light'
                                                          }} />
                                                      </Tooltip>
                                                    )} */}
                                                    <Tooltip title="Seitengröße">
                                                      <Chip
                                                        label={{
                                                          "1": "1",
                                                          "0.33": "1/3",
                                                          "0.5": "1/2",
                                                          "0.66": "2/3"
                                                        }[String(component.size)] || "N/A"}
                                                        color="success"
                                                        size="small"
                                                        sx={{
                                                          mr: '3px',
                                                          minWidth: '35px',
                                                          fontSize: '0.75rem',
                                                          height: '20px',
                                                          paddingTop: '1px',
                                                          bgcolor: 'success.light'
                                                        }}
                                                      />
                                                    </Tooltip>
                                                    {(!component.recurring && !component.isGroupHeader && !component.isDuplicate && !component.name.includes("Rückseite") && !component.name.includes("Deckblatt")) &&  (
                                                      <Tooltip title="Entfernen von Auswahl">
                                                        <IconButton
                                                          edge="end"
                                                          aria-label="remove"
                                                          color="error"
                                                          onClick={(e) => { e.stopPropagation(); removeFromCurrentChoice(component); }}
                                                          sx={{
                                                            padding: 0,
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            marginRight: '3px',
                                                            color: "#abaaaa",
                                                            '&:hover': {
                                                              color: 'error.light',
                                                              backgroundColor: 'transparent',
                                                            }
                                                          }}
                                                        >
                                                          <DeleteIcon />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                    </Box>
                                                  </Box>
                                                }
                                                secondaryTypographyProps={{
                                                  component: 'div',
                                                  sx: { width: '100%' }
                                                }}
                                                secondary={
                                                  (!component.isDuplicate && (component.isGroupHeader || component.recurring)) && (
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minHeight: '30px' }}>
                                                    {/* Linke Seite (Text & Input) */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      {(component.recurring && component.isGroupHeader) &&
                                                        `Seite ${findComponentPageRange(component, getSliceCount())}, Anzahl: ${component.duplicateIds?.length + 1}`
                                                      }
                                                
                                                      {/* Input Feld + Icon */}
                                                      {component.recurring && !component.isDuplicate && (
                                                        <>
                                                          <input
                                                            type="number"
                                                            min="1"
                                                            defaultValue={component.duplicateIds?.length || 1}
                                                            id={`duplicate-input-${component.uniqueId}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{
                                                              width: '50px',
                                                              padding: '2px',
                                                              textAlign: 'center',
                                                              borderRadius: '4px',
                                                              border: '1px solid #ddd',
                                                            }}
                                                          />
                                                          <Tooltip title="Duplizieren">
                                                            <IconButton
                                                              edge="end"
                                                              aria-label="duplicate"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                const inputElement = document.getElementById(`duplicate-input-${component.uniqueId}`);
                                                                const duplicateAmount = parseInt(inputElement.value, 10);
                                                                if (duplicateAmount > 0) {
                                                                  duplicateComponent(component, duplicateAmount);
                                                                }

                                                                // reset value to 1 after duplicate
                                                                setTimeout(() => {
                                                                  inputElement.value = "1";
                                                                }, 0);
                                                              }}
                                                              sx={{
                                                                color: "#abaaaa",
                                                                fontSize: '20px',
                                                                '&:hover': {
                                                                  color: 'success.light',
                                                                  backgroundColor: 'transparent',
                                                                }
                                                              }}
                                                            >
                                                              <LibraryAddIcon />
                                                            </IconButton>
                                                          </Tooltip>
                                                        </>
                                                      )}
                                                    </Box>
                                                
                                                    {/* Rechte Seite: DeleteIcon */}
                                                    {!component.isDuplicate && (
                                                      <Tooltip title="Entfernen von Auswahl">
                                                        <IconButton
                                                          edge="end"
                                                          aria-label="remove"
                                                          color="error"
                                                          onClick={(e) => { e.stopPropagation(); removeFromCurrentChoice(component); }}
                                                          sx={{
                                                            padding: 0,
                                                            fontSize: '0.75rem',
                                                            height: '20px',
                                                            marginRight: '3px',
                                                            color: "#abaaaa",
                                                            '&:hover': {
                                                              color: 'error.light',
                                                              backgroundColor: 'transparent',
                                                            }
                                                          }}
                                                        >
                                                          <DeleteIcon />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                  </Box>
                                                  )
                                                }                                            
                                              />
                                            </AccordionSummary>
                                    
                                            <AccordionDetails>
                                              <Typography variant="body2">{component.description}</Typography>
                                            </AccordionDetails>
                                          </Accordion>
                                        );
                                      }}
                                    </Draggable>
                                    ))}

                                    {providedRight.placeholder}
                                  </Box>
                                )}
                              </Droppable>

                              <Tooltip title="Leere Doppelseite hier einfügen">
                                <IconButton
                                  onClick={() => addEmptyDoublePage(i, getSliceCount())}
                                  sx={{
                                    position: "absolute",
                                    left: '50%',
                                    transform: "translateX(-50%)",
                                    mt: '5px',
                                    "--IconButton-hoverBg": "transparent",
                                  }}
                                  color="success"
                                >
                                  <AddCircleIcon
                                    sx={{
                                      color: "#abaaaa",
                                      fontSize: '20px',
                                      '&:hover': {
                                        color: 'success.light',
                                        backgroundColor: 'transparent',
                                      }
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </List>
                </Box>
              </Grid>

        <Box className="bottomWrapper">
          <Box className="printInfoBox">
          {getPagesCount(bookPages) > 120 ? (
            <Alert severity="error">
              Aufgrund einer drucktechnischen Vorgabe darf die maximale Seitenzahl von 120 nicht überschritten werden.
              Ihre aktuelle Seitenzahl beträgt <strong>{getPagesCount(bookPages)}</strong>. Bitte reduzieren Sie die Anzahl der Seiten.
            </Alert>
          ) : (
            <Alert severity="info">
              Aufgrund einer drucktechnischen Vorgabe sind nur bestimmte Seitenzahlen möglich.
              Alle fehlenden Seiten werden mit leeren Seiten aufgefüllt.
              <strong> Sie haben {getPagesCount(bookPages)} von {Math.ceil(getPagesCount(bookPages) / 6) * 6} Seiten befüllt.</strong>
            </Alert>
          )}
          </Box>
          <Box className="saveButton">
            <Box className="button1" sx= {{ mr: '1rem' }}>
            <button
              onClick={async () => {
                const { success, bookId: savedBookId, bookName: savedBookName } = await saveBookState(false, true);
                if (success) {
                  navigate('/pages/QuestionForm', {
                    state: {
                      bookId: savedBookId,
                      bookName: savedBookName,
                      primaryCategory: primaryPath,
                      isCreating: false,
                      editWithNewSelection: true,
                    }
                  });
                }
              }}
            >
              Speichern und mit neuen Kategorien fortfahren
            </button>
            </Box>
            <Box className="button2">
            <button onClick={() => saveBookState(false, false)}>Entwurf speichern</button>
            </Box>
          </Box>
        </Box>
        </DragDropContext>
      </Grid>
    </div>
  );
};